import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useParams, Link } from 'react-router-dom';
import clsx from 'clsx';
import {
  InfoIcon,
  HelpCircleIcon,
  CircleCheck,
  CircleX,
  Flag,
} from 'lucide-react';

import { useApi } from 'shared_components/context';
import { useWError } from 'shared_components/components/WError/WErrorProvider';
import {
  AdminPersonalInformation,
  PrimaryDiscographyRequestSortFieldsInner,
  PrimaryDiscographyRequestSortFieldsInnerFieldEnum,
  PrimaryDiscographyRequestFieldFiltersInner,
  ConfidenceStats,
  AvailableFiltersResponse,
  RecordingWestburyContributionsInner,
} from 'shared_components/generated/admin';
import type { MainArtistItem } from 'shared_components/utils/mainArtist';
import { AgCheckboxFilter } from './Components/AgCheckboxFilter';
import { AgTextFilter } from './Components/AgTextFilter';
import { buildCopyPayload, copyTextToClipboard } from './masterDiscographyCopy';
import WButton from 'shared_components/components/WForms/WButton/WButton';
import { WCheckBox } from 'shared_components/components/WCheckBox/WCheckBox';
import BulkEditModal from './Components/BulkEditModal';
import {
  MassEditFieldModal,
  MassTitleEditModal,
  MassDurationEditModal,
  LIVE_LOCKED_REASON,
  DURATION_LOCKED_REASON,
  type IsrcReassignmentMatches,
  type MassComplexField,
  type MassEdits,
  type MassFieldValue,
  type MassRowEdits,
} from './Components/MassEditFieldModals';
import { MassEditSummaryModal } from './Components/MassEditSummaryModal';
import {
  applyMassEdits,
  projectMassEditsOntoRow,
  type MassEditApplyResult,
  type MassIsrcReassignments,
} from './massEditBatch';
import { WError } from 'shared_components/components/WError/WError';
import PageLayout from './PageLayout';
import { MANUAL_CHANGE_BOX_SHADOW } from './manualChangeIndicator';
import WModal from 'shared_components/components/WModal';
import ThankyouBlock from 'shared_components/components/ThankYouBlock/ThankyouBlock';
import {
  RecordingEditForm,
  type RecordingEditFormHandle,
  type ReassignedIsrc,
} from './Components/RecordingEditForm';
import { VerificationChart } from './Components/ConfidenceAnalysisFilter';
import WDropdown from 'shared_components/components/WDropdownContent/WDropdown';
import TableLoadingOverlay from 'shared_components/components/TableLoadingOverlay';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import type {
  CellClickedEvent,
  Column,
  ColDef,
  ColSpanParams,
  ColumnState,
  FilterChangedEvent,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  IHeaderParams,
  PostSortRowsParams,
  RowClassParams,
  RowHeightParams,
  RowStyle,
  SortChangedEvent,
  SortDirection,
  SortModelItem,
} from 'ag-grid-community';
import { themeQuartz } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import {
  ChevronRight,
  ChevronDown,
  X,
  ChevronsLeft,
  ChevronsRight,
  ArrowUp,
  ArrowDown,
  Eye,
  EyeOff,
  Filter,
  Pencil,
} from 'lucide-react';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../components/ui/pagination';
import { ContributionPill } from './Components/ContributionPill';
import { allContributionRoles } from '../../data/contributionsTaxonomy';
import { allReleaseFormats } from '../../data/releaseFormatTaxonomy';
import { allReleaseTypes } from '../../data/releaseTypeTaxonomy';
import {
  isWriteOnceFieldEditable,
  liveToString,
} from '../../utils/recordingValidators';

// Register all Community features
ModuleRegistry.registerModules([AllCommunityModule]);

/** Bulk edit (one operation applied across many recordings) is still a
 *  prototype with no backend behind it — only mass edit has real endpoints, so
 *  the entry point is hidden. Flip to `true` to bring the button back; the mode
 *  itself is left intact below. */
const BULK_EDIT_ENABLED: boolean = false;

// to use myTheme in an application, pass it to the theme grid option
const myTheme = themeQuartz.withParams({
  backgroundColor: '#151719',
  browserColorScheme: 'dark',
  chromeBackgroundColor: '#151719',
  foregroundColor: '#FFFFFF',
  headerFontSize: 14,
  headerFontWeight: 'bold',
  headerRowBorder: false,
  wrapperBorder: false,
});

// Map ag-grid column IDs to API sort fields
const GRID_SORT_TO_API_FIELD: Partial<
  Record<string, PrimaryDiscographyRequestSortFieldsInnerFieldEnum>
> = {
  artist: 'parentRecording.bandArtistName.raw',
  title: 'parentRecording.recordingTitle.raw',
  release: 'earliestParentRelease.releaseTitle.raw',
  year: 'earliestParentRelease.dateReleased',
  format: 'earliestParentRelease.format',
  type: 'earliestParentRelease.mediaType',
  stageName: 'persona.stageName',
  // `live` is now the free-text descriptor string, sorted alphanumerically
  // (was the boolean `parentRecording.isLive`).
  live: 'parentRecording.live',
  contributions: 'westburyContributions.contribution',
  isrcs: 'parentRecording.isrcs',
  duration: 'parentRecording.duration',
  featured: 'performance.featuredStatus',
  collaborators: 'parentRecording.bandArtistName.raw',
  version: 'parentRecording.recordingVersion',
  label: 'earliestParentRelease.releaseLabels',
  catNumbers: 'earliestParentRelease.catNumbers',
};

// Map ag-grid column IDs to API filter field names
const GRID_COL_TO_API_FILTER_FIELD: Partial<Record<string, string>> = {
  artist: 'parentRecording.bandArtistName.raw',
  version: 'parentRecording.recordingVersion',
  contributions: 'westburyContributions.contribution',
  release: 'earliestParentRelease.releaseTitle.raw',
  year: 'earliestParentRelease.dateReleased',
  live: 'parentRecording.live',
  title: 'parentRecording.recordingTitle.raw',
  isrcs: 'parentRecording.isrcs',
  stageName: 'persona.stageName',
  type: 'earliestParentRelease.mediaType',
  format: 'earliestParentRelease.format',
  catNumbers: 'earliestParentRelease.catNumbers',
  label: 'earliestParentRelease.releaseLabels',
  featured: 'performance.featuredStatus',
};

// Default sort order: Format, Year of Release, Live, Release, Version, Recording Title, Artist
const DEFAULT_SORT_MODEL: SortModelItem[] = [
  // { colId: 'format', sort: 'asc' },
  { colId: 'year', sort: 'asc' },
  { colId: 'live', sort: 'asc' },
  { colId: 'release', sort: 'asc' },
  { colId: 'version', sort: 'asc' },
  { colId: 'title', sort: 'asc' },
  { colId: 'artist', sort: 'asc' },
];

interface Row {
  id: string;
  artist?: string | null;
  title: string;
  version?: string | null;
  contributions: RecordingWestburyContributionsInner[];
  isrcs: string;
  year?: string | null;
  release: string;
  duration?: string | null;
  isFeatured: boolean;
  isLive: boolean;
  /** Live descriptor string (OS schema: ParentRecording.live). Empty/null
   *  means the recording is not live. `isLive` is derived from its presence. */
  live?: string | null;
  // Subheadings (shown in expanded detail)
  collaborators: string;
  label: string;
  catNumbers: string;
  format: string;
  mediaType: string;
  stageName: string;
  location: string;
  source: string;
  labelsArray: string[];
  catNumbersArray: string[];
  versionsArray: string[];
  isrcsArray: string[];
  mainArtistsRaw: (string | MainArtistItem)[];
  documentId?: string;
  earliestParentReleaseId?: string;
  sourceReleaseId?: string[];
  earliestReleaseImageKey?: string;
  releaseImageKeys: string[];
  isModified?: boolean;
  originalValues?: Partial<Row>;
  /** True when the recording has any tracked manual edits (sourced from
   *  the bulk manual-changes endpoint, contract 4.5.1). */
  hasManualChanges?: boolean;
  /** True when the `live` descriptor was manually changed before (per-field
   *  manual-changes overlay). Gates editing an already-set live descriptor in
   *  mass/bulk — mirrors the edit form's isFieldEdited('live'). */
  liveEditedManually?: boolean;
  /** True when `duration` was manually changed before. Gates editing an
   *  already-set duration (write-once unless previously manually edited). */
  durationEditedManually?: boolean;
  /** Artists added by a manual change (contract 4.5.2 `fields.artists.items`
   *  with `op: add`) — drives the purple line on the mass-edit artist chips,
   *  matching the edit form. Names are kept alongside ids because legacy rows
   *  carry stage names only. */
  manuallyAddedArtistIds?: string[];
  manuallyAddedArtistNames?: string[];
  /** Contribution roles added or edited by a manual change (contract 4.5.2
   *  `fields.contributions.items` with `op: add | edit`) — drives the purple
   *  line on the mass-edit contribution chips, matching the edit form. */
  manuallyChangedContributionRoles?: string[];
  /** ISRCs added by a manual change (contract 4.5.2 `fields.isrcs.items` with
   *  `op: add`) — drives the purple line on the mass-edit ISRC chips. */
  manuallyAddedIsrcs?: string[];
}

type GridRow =
  | (Row & {
      kind: 'data';
    })
  | {
      kind: 'detail';
      id: string;
      parentId: string;
      base: Row;
    };

// --- Mass-edit cell rendering (PROTOTYPE) ---------------------------------
// A pulsating purple dot in the top-right corner marks a cell with a pending
// mass edit (mirrors the recording edit form's "unsaved change" indicator).
const MASS_DOT_CLASS =
  'tw-absolute tw-top-1 tw-right-1 tw-w-2.5 tw-h-2.5 tw-rounded-full tw-bg-surfacePurple tw-border tw-border-gray-900 tw-animate-pulse-slow tw-z-10 tw-pointer-events-none';

/** Edit affordance shown on cell hover — hidden by default, revealed via the
 *  parent's `tw-group` hover. Clicking it starts editing (inline input or a
 *  quick-edit modal) without triggering the cell's other click behaviour.
 *
 *  A locked field still shows the pencil so the affordance doesn't silently
 *  disappear, but greyed out and non-interactive. The `title` and the
 *  not-allowed cursor sit on the wrapper because a `disabled` button receives
 *  no mouse events, so neither would ever show on the button itself. */
const MassEditPencil: React.FC<{
  onEdit: () => void;
  /** Render disabled — `disabledReason` explains why in the tooltip. */
  disabled?: boolean;
  disabledReason?: string;
}> = ({ onEdit, disabled = false, disabledReason }) =>
  disabled ? (
    <span
      title={disabledReason}
      className="tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-flex-shrink-0 tw-ml-1 tw-inline-flex tw-cursor-not-allowed"
    >
      <span
        aria-label="Edit (locked)"
        aria-disabled="true"
        role="button"
        className="tw-p-1 tw-rounded tw-text-textSeal tw-opacity-50 tw-pointer-events-none"
      >
        <Pencil className="tw-w-3.5 tw-h-3.5" />
      </span>
    </span>
  ) : (
    <button
      type="button"
      aria-label="Edit"
      title="Edit"
      className="tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-flex-shrink-0 tw-ml-1 tw-p-1 tw-rounded hover:tw-bg-gray-700 tw-text-textDove hover:tw-text-white"
      onClick={(e) => {
        e.stopPropagation();
        onEdit();
      }}
    >
      <Pencil className="tw-w-3.5 tw-h-3.5" />
    </button>
  );

/** Read-only cell content with a pending-change dot (used for cells that can't
 *  be edited, e.g. an empty Version or a write-once Duration already set). */
const MassCellShell: React.FC<{
  changed: boolean;
  children: React.ReactNode;
}> = ({ changed, children }) => (
  <div className="tw-relative tw-flex tw-items-center tw-w-full tw-h-full">
    <span className="tw-truncate">{children}</span>
    {changed && (
      <span aria-hidden title="Pending change" className={MASS_DOT_CLASS} />
    )}
  </div>
);

/** Wrapper for a complex field (Artist, Version, Contributions, Live, ISRCs).
 *  The edit pencil appears on hover and opens the quick-edit modal; the rest of
 *  the cell keeps its normal click behaviour (e.g. copy). */
const MassComplexCellShell: React.FC<{
  changed: boolean;
  onOpen: () => void;
  /** Locked field — the pencil still renders, greyed out and inert. */
  disabled?: boolean;
  disabledReason?: string;
  children: React.ReactNode;
}> = ({ changed, onOpen, disabled = false, disabledReason, children }) => (
  <div className="tw-relative tw-flex tw-items-center tw-w-full tw-h-full tw-group">
    <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0 tw-flex-1">
      {children}
    </div>
    <MassEditPencil
      onEdit={onOpen}
      disabled={disabled}
      disabledReason={disabledReason}
    />
    {changed && (
      <span aria-hidden title="Pending change" className={MASS_DOT_CLASS} />
    )}
  </div>
);

/** Render the display content of a complex mass-edit cell — the pending value
 *  if one exists, otherwise the recording's current value. */
function renderMassComplexContent(
  field: MassComplexField,
  row: Row,
  pending: MassFieldValue | undefined
): React.ReactNode {
  switch (field) {
    case 'live': {
      // Matches the read-only Live column: the free-text descriptor itself
      // (e.g. "Live in London, 1989"), or '-' when the recording isn't live.
      const descriptor =
        pending !== undefined ? (pending as string).trim() : row.live ?? '';
      return <span className="tw-truncate">{descriptor || '-'}</span>;
    }
    case 'version': {
      const versions =
        pending !== undefined ? (pending as string[]) : row.versionsArray ?? [];
      return <span className="tw-truncate">{versions.join(', ') || '-'}</span>;
    }
    case 'isrcs': {
      const isrcs =
        pending !== undefined ? (pending as string[]) : row.isrcsArray ?? [];
      return <span className="tw-truncate">{isrcs.join(', ') || '-'}</span>;
    }
    case 'contributions': {
      const contributions =
        pending !== undefined
          ? (pending as RecordingWestburyContributionsInner[])
          : row.contributions ?? [];
      return (
        <div className="tw-flex tw-gap-2 tw-overflow-hidden">
          {contributions.map((c) => (
            <ContributionPill key={c.contribution} contribution={c} />
          ))}
        </div>
      );
    }
    case 'artist': {
      // Demoted artists are tombstones carried for the save layer — the cell,
      // like the editor, only lists featured / other-featured artists.
      const names =
        pending !== undefined
          ? (pending as { name: string; demoted?: boolean }[])
              .filter((a) => !a.demoted)
              .map((a) => a.name)
              .join(', ')
          : row.artist ?? '';
      return <span className="tw-truncate">{names || '-'}</span>;
    }
    default:
      return null;
  }
}

type RowIdImageUrlMap = {
  [id: string]: string;
};

type RouteParams = {
  userId: string;
};

// Detail row data type
type DetailRowData = {
  artist: string;
  title: string;
  version: string;
  contributions: RecordingWestburyContributionsInner[];
  isrcs: string;
  release: string;
  year: string;
  duration: string;
  type: string;
  stageName: string;
  isLive: boolean;
  isFeatured: boolean;
  collaborators: string;
  label: string;
  catNumbers: string;
  format: string;
};

// Map DetailGrid field names to main grid column IDs
const DETAIL_FIELD_TO_MAIN_COL_ID: Partial<Record<string, string>> = {
  collaborators: 'artist', // Collaborators maps to artist column
  format: 'format',
  type: 'type',
  stageName: 'stageName',
  // label and catNumbers don't have corresponding main grid columns
};

// Combined Column Visibility Dropdown Component
const CombinedColumnVisibilityDropdown: React.FC<{
  gridApi: GridApi | null;
  columnDefs: ColDef[];
  detailColumnDefs: ColDef<DetailRowData>[];
  onDetailColumnVisibilityChange: (field: string, visible: boolean) => void;
  detailVisibleColumns: Set<string>;
}> = ({
  gridApi,
  columnDefs,
  detailColumnDefs,
  onDetailColumnVisibilityChange,
  detailVisibleColumns,
}) => {
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    // Initialize with all non-hidden columns
    const visible = new Set<string>();
    columnDefs.forEach((col) => {
      if (col.colId && !col.hide) {
        visible.add(col.colId);
      }
    });
    return visible;
  });

  // Update visible columns when columnDefs change
  useEffect(() => {
    if (!gridApi) return;
    const columnState = gridApi.getColumnState();
    const newVisible = new Set<string>();
    columnState.forEach((col) => {
      if (col.colId && !col.hide) {
        newVisible.add(col.colId);
      }
    });
    setVisibleColumns(newVisible);
  }, [gridApi, columnDefs]);

  const toggleMainColumnVisibility = (colId: string) => {
    if (!gridApi || !colId) return;

    const isVisible = visibleColumns.has(colId);
    const newVisible = new Set(visibleColumns);

    if (isVisible) {
      newVisible.delete(colId);
    } else {
      newVisible.add(colId);
    }

    setVisibleColumns(newVisible);
    gridApi.setColumnsVisible([colId], !isVisible);
  };

  const toggleDetailColumnVisibility = (field: string) => {
    const isVisible = detailVisibleColumns.has(field);
    onDetailColumnVisibilityChange(field, !isVisible);
  };

  // Filter out expand column and get displayable columns
  const displayableMainColumns = columnDefs.filter(
    (col) => col.colId && col.colId !== 'expand'
  );
  const displayableDetailColumns = detailColumnDefs.filter((col) => col.field);

  return (
    <WDropdown
      trigger={<WButton variant="secondary" label="Show/Hide Data" />}
      content={
        <div className="tw-min-w-[200px] tw-max-h-[500px] tw-overflow-y-auto">
          {/* Main Columns Section */}
          <div className="tw-px-2 tw-py-2 tw-text-xs tw-font-semibold tw-text-neutral-400 tw-uppercase tw-tracking-wide">
            Top-Level
          </div>
          {displayableMainColumns.map((col) => {
            const colId = col.colId!;
            const isVisible = visibleColumns.has(colId);
            return (
              <div
                key={`main-${colId}`}
                role="button"
                tabIndex={0}
                className="tw-px-2 tw-py-2 tw-cursor-pointer hover:tw-bg-neutral-800 tw-flex tw-items-center tw-gap-2"
                onClick={() => toggleMainColumnVisibility(colId)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleMainColumnVisibility(colId);
                  }
                }}
              >
                {isVisible ? (
                  <Eye className="tw-w-4 tw-h-4 tw-text-white" />
                ) : (
                  <EyeOff className="tw-w-4 tw-h-4 tw-text-neutral-500" />
                )}
                <span
                  className={
                    isVisible ? 'tw-text-white' : 'tw-text-neutral-500'
                  }
                >
                  {col.headerName || colId}
                </span>
              </div>
            );
          })}

          {/* Separator */}
          <div className="tw-px-2 tw-py-1 tw-border-t tw-border-neutral-700 tw-my-1" />

          {/* Detail Columns Section */}
          <div className="tw-px-2 tw-py-2 tw-text-xs tw-font-semibold tw-text-neutral-400 tw-uppercase tw-tracking-wide">
            Sub-Level
          </div>
          {displayableDetailColumns.map((col) => {
            const field = col.field!;
            const isVisible = detailVisibleColumns.has(field);
            return (
              <div
                key={`detail-${field}`}
                role="button"
                tabIndex={0}
                className="tw-px-2 tw-py-2 tw-cursor-pointer hover:tw-bg-neutral-800 tw-flex tw-items-center tw-gap-2"
                onClick={() => toggleDetailColumnVisibility(field)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleDetailColumnVisibility(field);
                  }
                }}
              >
                {isVisible ? (
                  <Eye className="tw-w-4 tw-h-4 tw-text-white" />
                ) : (
                  <EyeOff className="tw-w-4 tw-h-4 tw-text-neutral-500" />
                )}
                <span
                  className={
                    isVisible ? 'tw-text-white' : 'tw-text-neutral-500'
                  }
                >
                  {col.headerName || field}
                </span>
              </div>
            );
          })}
        </div>
      }
    />
  );
};

// Detail grid component
const DetailGrid: React.FC<{
  row: Row;
  onSortChange?: (colId: string, sort: SortDirection | null) => void;
  rowId: string;
  onGridReady?: (rowId: string, api: GridApi<DetailRowData>) => void;
  visibleColumns: Set<string>;
  onCellClickedForCopy?: (
    api: GridApi<DetailRowData>,
    rowIndex: number,
    colId: string
  ) => void;
}> = ({
  row,
  onSortChange,
  rowId,
  onGridReady,
  visibleColumns,
  onCellClickedForCopy,
}) => {
  const detailGridApiRef = useRef<GridApi<DetailRowData> | null>(null);

  const detailColumnDefs = useMemo<ColDef<DetailRowData>[]>(
    () => [
      {
        headerName: 'Artist',
        hide: true,
        field: 'artist',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Recording Title',
        hide: true,
        field: 'title',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Version',
        hide: true,
        field: 'version',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Contributions',
        hide: true,
        field: 'contributions',
        minWidth: 150,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        cellRenderer: (params: ICellRendererParams<DetailRowData>) => {
          if (!params.data) {
            return null;
          }
          return (
            <div className="tw-flex tw-gap-2">
              {params.data.contributions.map((contribution) => (
                <ContributionPill
                  key={contribution.contribution}
                  contribution={contribution}
                />
              ))}
            </div>
          );
        },
      },
      {
        headerName: 'ISRCs',
        hide: true,
        field: 'isrcs',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Release',
        hide: true,
        field: 'release',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Year',
        hide: true,
        field: 'year',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Duration',
        hide: true,
        field: 'duration',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Live',
        hide: true,
        field: 'isLive',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },

      {
        headerName: 'Collaborators',
        field: 'collaborators',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Label',
        field: 'label',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Catalogue Number',
        field: 'catNumbers',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Format',
        field: 'format',
        minWidth: 100,
        sortable: true,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Type',
        field: 'type',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Stage Name',
        field: 'stageName',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Featured',
        field: 'isFeatured',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        cellRenderer: (params: ICellRendererParams<DetailRowData>) => {
          if (!params.data) {
            return null;
          }
          return (
            <div>
              <i
                className={clsx('wi wi-xs', {
                  'wi-check': params.data.isFeatured,
                  'wi-close': !params.data.isFeatured,
                })}
              />
            </div>
          );
        },
      },
    ],
    []
  );

  const detailRowData = useMemo<DetailRowData[]>(
    () => [
      {
        artist: row.artist || '—',
        title: row.title || '—',
        version: row.version || '—',
        contributions: row.contributions || '—',
        isrcs: row.isrcs || '—',
        release: row.release || '—',
        year: row.year || '—',
        duration: row.duration || '—',
        type: row.mediaType || '—',
        stageName: row.stageName || '—',
        isLive: row.isLive || false,
        isFeatured: row.isFeatured || false,
        collaborators: row.collaborators || '—',
        label: row.label || '—',
        catNumbers: row.catNumbers || '—',
        format: row.format || '—',
      },
    ],
    [row]
  );

  const detailDefaultColDef = useMemo<ColDef<DetailRowData>>(
    () => ({
      sortable: true,
      resizable: true,
      suppressMenu: true,
      suppressMovable: false,
      sortingOrder: ['asc', 'desc', null],
    }),
    []
  );

  const handleDetailSortChanged = useCallback(
    (event: SortChangedEvent<DetailRowData>) => {
      if (!event.api || !onSortChange) {
        return;
      }

      const sortedColumns = event.api
        .getColumnState()
        .filter(
          (col) =>
            col.sort && col.sortIndex !== null && col.sortIndex !== undefined
        )
        .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0));

      // Get the primary sorted column (first one)
      const primaryColumn = sortedColumns[0];
      if (primaryColumn && primaryColumn.colId) {
        const mainColId = DETAIL_FIELD_TO_MAIN_COL_ID[primaryColumn.colId];
        if (mainColId && primaryColumn.sort) {
          // Trigger sort in main grid
          onSortChange(mainColId, primaryColumn.sort);
        }
      } else if (sortedColumns.length === 0) {
        // No sort - could reset to default, but for now do nothing
        // as DetailGrid sort shouldn't clear main grid sort
      }
    },
    [onSortChange]
  );

  const handleDetailGridReady = useCallback(
    (params: GridReadyEvent<DetailRowData>) => {
      detailGridApiRef.current = params.api;
      if (onGridReady) {
        onGridReady(rowId, params.api);
      }
      // Apply initial column visibility
      detailColumnDefs.forEach((colDef) => {
        if (colDef.field) {
          const shouldBeVisible = visibleColumns.has(colDef.field);
          params.api.setColumnsVisible([colDef.field], shouldBeVisible);
        }
      });
    },
    [onGridReady, rowId, detailColumnDefs, visibleColumns]
  );

  return (
    <div
      className="detail-grid-wrapper tw-w-full tw-h-full tw-rounded-b tw-p-3"
      style={{ width: '100%', height: '100%' }}
    >
      <div style={{ height: '60px', width: '100%', overflow: 'hidden' }}>
        <AgGridReact
          rowData={detailRowData}
          columnDefs={detailColumnDefs}
          defaultColDef={detailDefaultColDef}
          headerHeight={30}
          rowHeight={30}
          domLayout="normal"
          suppressHorizontalScroll={false}
          onSortChanged={handleDetailSortChanged}
          onGridReady={handleDetailGridReady}
          onCellClicked={(event) => {
            if (event.rowIndex != null && onCellClickedForCopy) {
              onCellClickedForCopy(
                event.api,
                event.rowIndex,
                event.column.getColId()
              );
            }
          }}
          alwaysMultiSort={false}
          suppressRowHoverHighlight={true}
        />
      </div>
    </div>
  );
};

function MasterDiscography() {
  const { userId } = useParams<RouteParams>();
  const { adminApi: api } = useApi();
  const { throwError } = useWError();

  const [clientPersonalInfo, setClientPersonalInfo] =
    useState<AdminPersonalInformation>();
  const [isPendingExportGeneration, setIsPendingExportGeneration] =
    useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingError, setLoadingError] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [images, setImages] = useState<RowIdImageUrlMap>({});
  const [confidenceStats, setConfidenceStats] =
    useState<ConfidenceStats | null>(null);
  const [selectedConfidenceCategory, setSelectedConfidenceCategory] = useState<
    string | null
  >(null);
  const [availableFilters, setAvailableFilters] =
    useState<AvailableFiltersResponse | null>(null);

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');

  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(50);
  const [recordingsCount, setRecordingsCount] = useState<number>(0);

  const [isOpenExportConfirmationModal, setIsOpenExportConfirmationModal] =
    useState<boolean>(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [selectedRowForModal, setSelectedRowForModal] = useState<Row | null>(
    null
  );
  // Row the user wants to switch to while the currently-open recording has
  // unsaved changes — pending a discard/continue confirmation.
  const [pendingSwitchRow, setPendingSwitchRow] = useState<Row | null>(null);

  const mainContentColumnRef = useRef<HTMLDivElement>(null);
  const editFormRef = useRef<RecordingEditFormHandle>(null);
  // Mirror of the currently-open row id so handleOpenEditModal can detect a
  // switch without taking selectedRowForModal as a useCallback dependency
  // (which would rebuild the grid column defs on every selection change).
  const selectedRowIdRef = useRef<string | null>(null);
  useEffect(() => {
    selectedRowIdRef.current = selectedRowForModal?.id ?? null;
  }, [selectedRowForModal]);
  const [panelBounds, setPanelBounds] = useState<{
    top: number;
    height: number;
  } | null>(null);

  const sortFieldsRef = useRef<PrimaryDiscographyRequestSortFieldsInner[]>([]);
  const [sortFields, setSortFields] = useState<
    PrimaryDiscographyRequestSortFieldsInner[]
  >(() => {
    // Build default sortFields from DEFAULT_SORT_MODEL
    const defaultSortFields = DEFAULT_SORT_MODEL.map((item) => {
      const apiField = GRID_SORT_TO_API_FIELD[item.colId];
      if (apiField && item.sort) {
        return {
          field: apiField,
          order: item.sort === 'desc' ? 'desc' : 'asc',
        };
      }
      return null;
    }).filter(
      (item): item is PrimaryDiscographyRequestSortFieldsInner => item !== null
    );
    sortFieldsRef.current = defaultSortFields;
    return defaultSortFields;
  });
  const [fieldFilters, setFieldFilters] = useState<
    PrimaryDiscographyRequestFieldFiltersInner[]
  >([]);
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(
    () => new Set()
  );
  const gridApiRef = useRef<GridApi<GridRow> | null>(null);

  // --- Bulk / mass editing (PROTOTYPE) -------------------------------------
  // Toggles the whole table into bulk-edit mode: filtering & pagination are
  // disabled, all rows load on a single page, the Actions column is swapped
  // for a selection checkbox column, and the toolbar shows bulk actions only.
  const [bulkEditMode, setBulkEditMode] = useState<boolean>(false);
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(
    () => new Set()
  );
  const [showBulkEditModal, setShowBulkEditModal] = useState<boolean>(false);
  // Client aliases (personas) for the contribution editor in the bulk modal.
  const [bulkClientAliases, setBulkClientAliases] = useState<
    { stageName: string; personaId: string }[]
  >([]);
  // Remember the page size used before entering bulk mode so we can restore it
  // on exit (bulk mode forces a single page containing every record).
  const preBulkRowsPerPageRef = useRef<number>(rowsPerPage);
  // Mirror selection + rows into refs so cell/header renderers can read the
  // latest value without rebuilding columnDefs on every change.
  const selectedRowIdsRef = useRef<Set<string>>(selectedRowIds);
  const prevSelectedRowIdsRef = useRef<Set<string>>(selectedRowIds);
  useEffect(() => {
    const prev = prevSelectedRowIdsRef.current;
    const curr = selectedRowIds;
    selectedRowIdsRef.current = curr;
    prevSelectedRowIdsRef.current = curr;

    const api = gridApiRef.current;
    if (!api) return;

    // Redraw the rows whose selection state flipped so both the checkbox cell
    // and the row-level "selected" background (getRowStyle) are re-applied —
    // refreshCells alone doesn't re-run getRowStyle.
    const changedIds = new Set<string>();
    prev.forEach((id) => {
      if (!curr.has(id)) changedIds.add(id);
    });
    curr.forEach((id) => {
      if (!prev.has(id)) changedIds.add(id);
    });

    const changedNodes = Array.from(changedIds)
      .map((id) => api.getRowNode(id))
      .filter((node): node is NonNullable<typeof node> => node != null);
    if (changedNodes.length > 0) {
      api.redrawRows({ rowNodes: changedNodes });
    }
    // Keep the select-all header checkbox in sync.
    api.refreshHeader();
  }, [selectedRowIds]);
  const rowsRef = useRef<Row[]>(rows);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  // --- Mass editing (PROTOTYPE) --------------------------------------------
  // Recording-level cells are edited individually — inline for simple fields
  // (title, duration) and via reused modals for complex ones (artist, version,
  // contributions, live, isrcs). The edit affordance is revealed on cell hover;
  // there is no top-level toggle. The moment the first cell is edited the table
  // "enters" mass-edit mode (mass edit mode is derived from having any pending
  // edit) which locks the toolbar to Save/Cancel and freezes the current page.
  // Mass edit always works with the current page only — we never fetch all.
  const [massEdits, setMassEdits] = useState<MassEdits>({});
  const massEditsRef = useRef<MassEdits>(massEdits);
  // Which complex field/recording is currently open in a quick-edit modal.
  const [massFieldTarget, setMassFieldTarget] = useState<{
    rowId: string;
    field: MassComplexField;
  } | null>(null);
  // Which single-value field (Title/Duration) is open in its own modal editor.
  // Both are modal-only rather than inline: their rules need more room than a
  // grid cell can give.
  const [massTextTarget, setMassTextTarget] = useState<{
    rowId: string;
    field: 'title' | 'duration';
  } | null>(null);
  const [showMassSummary, setShowMassSummary] = useState<boolean>(false);
  // ISRCs reassigned onto a recording during this mass edit, keyed by the
  // recording gaining them. Carried into the `add isrcs` edit as `dup_isrcs`
  // so the backend detaches each ISRC from its source recording.
  const [massIsrcReassignments, setMassIsrcReassignments] =
    useState<MassIsrcReassignments>({});
  // In-flight state + outcome of the batch apply, surfaced by the summary modal.
  const [massSaving, setMassSaving] = useState<boolean>(false);
  const [massApplyResult, setMassApplyResult] =
    useState<MassEditApplyResult | null>(null);

  // Keep the ref in sync and re-render the visible cells (so the purple
  // "pending change" dot appears/disappears) whenever pending edits change.
  useEffect(() => {
    massEditsRef.current = massEdits;
    gridApiRef.current?.refreshCells({ force: true });
  }, [massEdits]);

  // Total number of pending field changes across all recordings.
  const massEditFieldCount = useMemo(
    () =>
      Object.values(massEdits).reduce(
        (sum, rowEdits) => sum + Object.keys(rowEdits).length,
        0
      ),
    [massEdits]
  );
  // Mass-edit mode is derived: the table is "in" mass edit as soon as there is
  // at least one pending edit, and reverts to normal browsing when the user
  // saves or cancels (which clears the pending edits).
  const massEditMode = massEditFieldCount > 0;

  // Record (or clear, when reverted to the original) a pending edit for one
  // field of one recording. `changed` decides whether it's stored or dropped.
  const setMassCellEdit = useCallback(
    (
      rowId: string,
      field: keyof MassEdits[string],
      value: MassEdits[string][keyof MassEdits[string]],
      changed: boolean
    ) => {
      setMassEdits((prev) => {
        const rowEdits = { ...(prev[rowId] ?? {}) };
        if (changed) {
          rowEdits[field] = value as never;
        } else {
          delete rowEdits[field];
        }
        const next = { ...prev };
        if (Object.keys(rowEdits).length > 0) {
          next[rowId] = rowEdits;
        } else {
          delete next[rowId];
        }
        massEditsRef.current = next;
        return next;
      });
    },
    []
  );

  const openMassFieldModal = useCallback(
    (rowId: string, field: MassComplexField) => {
      setMassFieldTarget({ rowId, field });
    },
    []
  );

  // Save the value returned by a complex-field quick-edit modal, storing it as
  // a pending edit only when it actually differs from the recording's current
  // value.
  const handleMassFieldSave = useCallback(
    (row: Row, field: MassComplexField, value: MassFieldValue) => {
      let original: unknown;
      switch (field) {
        case 'live':
          original = typeof row.live === 'string' ? row.live.trim() : '';
          break;
        case 'version':
          original = row.versionsArray ?? [];
          break;
        case 'isrcs':
          original = row.isrcsArray ?? [];
          break;
        case 'contributions':
          original = (row.contributions ?? []).map((c) => ({
            contribution: c.contribution,
            confidenceCategory: c.confidenceCategory ?? '1',
            confidencePattern: c.confidencePattern ?? '',
          }));
          break;
        case 'artist':
          original = (row.mainArtistsRaw ?? []).map((item) =>
            typeof item === 'string' ? item : item.stageName
          );
          break;
        default:
          original = undefined;
      }
      const changed =
        field === 'artist'
          ? // Compare names *and* the demote flag: a demoted artist stays in the
            // list, so a name-only comparison would treat the demotion as a
            // no-op and silently drop it.
            JSON.stringify(
              (value as { name: string; demoted?: boolean }[]).map((a) => [
                a.name,
                !!a.demoted,
              ])
            ) !== JSON.stringify((original as string[]).map((n) => [n, false]))
          : JSON.stringify(value) !== JSON.stringify(original);
      setMassCellEdit(row.id, field, value, changed);
      setMassFieldTarget(null);
    },
    [setMassCellEdit]
  );

  // When an ISRC is reassigned to the recording being edited we do two things:
  // remember the source recordings so the `add isrcs` edit can carry
  // `dup_isrcs` when the mass edit is applied, and optimistically strip the
  // ISRC from those source rows — but only the ones currently loaded on the
  // page, so the user sees the ISRC move rather than appear twice.
  const handleReassignIsrcs = useCallback(
    (targetRowId: string, reassignments: IsrcReassignmentMatches) => {
      setMassIsrcReassignments((prev) => ({
        ...prev,
        [targetRowId]: {
          ...(prev[targetRowId] ?? {}),
          ...Object.fromEntries(
            Object.entries(reassignments).map(([isrc, matches]) => [
              isrc,
              matches.map((m) => ({
                recordingId: m.recordingId,
                documentId: m.documentId,
              })),
            ])
          ),
        },
      }));

      Object.entries(reassignments).forEach(([isrc, matches]) => {
        matches.forEach(({ recordingId: srcId }) => {
          const srcRow = rowsRef.current.find((r) => r.id === srcId);
          if (!srcRow) return; // source recording isn't on the current page
          const currentList =
            massEditsRef.current[srcId]?.isrcs ?? srcRow.isrcsArray ?? [];
          const nextList = currentList.filter(
            (x) => x.toLowerCase() !== isrc.toLowerCase()
          );
          if (nextList.length === currentList.length) return; // not present
          const original = srcRow.isrcsArray ?? [];
          const changed = JSON.stringify(nextList) !== JSON.stringify(original);
          setMassCellEdit(srcId, 'isrcs', nextList, changed);
        });
      });
    },
    [setMassCellEdit]
  );

  // Discard a single pending edit (used by the review summary's "Remove").
  const removeMassCellEdit = useCallback(
    (rowId: string, field: keyof MassRowEdits) => {
      setMassEdits((prev) => {
        if (!prev[rowId] || prev[rowId][field] === undefined) return prev;
        const rowEdits = { ...prev[rowId] };
        delete rowEdits[field];
        const next = { ...prev };
        if (Object.keys(rowEdits).length > 0) {
          next[rowId] = rowEdits;
        } else {
          delete next[rowId];
        }
        massEditsRef.current = next;
        return next;
      });
    },
    []
  );

  // Persist a Title/Duration edit, dropping it if it reverts to the
  // recording's original value.
  const handleMassTextSave = useCallback(
    (row: Row, field: 'title' | 'duration', value: string) => {
      const nv = value.trim();
      if (field === 'title') {
        setMassCellEdit(row.id, 'title', nv, nv !== row.title);
      } else {
        setMassCellEdit(row.id, 'duration', nv, nv !== (row.duration ?? ''));
      }
    },
    [setMassCellEdit]
  );

  // Route the summary's "Edit" to the right editor: complex fields reuse the
  // grid's quick-edit modal; Title/Duration use their own.
  const handleSummaryEditChange = useCallback(
    (rowId: string, field: keyof MassRowEdits) => {
      if (field === 'title' || field === 'duration') {
        setMassTextTarget({ rowId, field });
      } else {
        setMassFieldTarget({ rowId, field });
      }
    },
    []
  );
  // -------------------------------------------------------------------------

  const prevDebouncedSearchTermRef = useRef<string>('');
  const isInitialMountRef = useRef<boolean>(true);
  const isApplyingInitialSortRef = useRef<boolean>(false);

  // Transient "copied to clipboard" popup state.
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const copyFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  // The last cell the user clicked in either the main grid or an expanded
  // detail grid. Used to resolve the copy target (with its owning grid api).
  const copySourceRef = useRef<{
    api: GridApi<GridRow> | GridApi<DetailRowData>;
    rowIndex: number;
    colId: string;
  } | null>(null);

  // Serialize arrays for stable dependency comparison
  const sortFieldsKey = useMemo(() => JSON.stringify(sortFields), [sortFields]);
  const fieldFiltersKey = useMemo(
    () => JSON.stringify(fieldFilters),
    [fieldFilters]
  );

  // Track detail grid APIs for column visibility control
  const detailGridApiRefs = useRef<Map<string, GridApi<DetailRowData>>>(
    new Map()
  );

  // Measure main content column so the side panel height matches the table area
  useEffect(() => {
    if (
      !showEditPanel ||
      !selectedRowForModal ||
      !mainContentColumnRef.current
    ) {
      setPanelBounds(null);
      return;
    }
    const el = mainContentColumnRef.current;
    const updateBounds = () => {
      const rect = el.getBoundingClientRect();
      setPanelBounds({ top: rect.top, height: rect.height });
    };
    updateBounds();
    const ro = new ResizeObserver(updateBounds);
    ro.observe(el);
    return () => ro.disconnect();
  }, [showEditPanel, selectedRowForModal]);

  // Detail grid column visibility state - initialized from detailColumnDefs
  const detailColumnDefsForVisibility = useMemo<ColDef<DetailRowData>[]>(
    () => [
      {
        headerName: 'Artist',
        hide: true,
        field: 'artist',
      },
      {
        headerName: 'Recording Title',
        hide: true,
        field: 'title',
      },
      {
        headerName: 'Version',
        hide: true,
        field: 'version',
      },
      {
        headerName: 'Contributions',
        hide: true,
        field: 'contributions',
      },
      {
        headerName: 'ISRCs',
        hide: true,
        field: 'isrcs',
      },
      {
        headerName: 'Release',
        hide: true,
        field: 'release',
      },
      {
        headerName: 'Year',
        hide: true,
        field: 'year',
      },
      {
        headerName: 'Duration',
        hide: true,
        field: 'duration',
      },
      {
        headerName: 'Type',
        hide: true,
        colId: 'type',
      },
      {
        headerName: 'Stage Name',
        hide: true,
        field: 'stageName',
      },
      {
        headerName: 'Live',
        hide: true,
        field: 'isLive',
      },
      {
        headerName: 'Featured',
        field: 'isFeatured',
      },
      {
        headerName: 'Collaborators',
        field: 'collaborators',
      },
      {
        headerName: 'Label',
        field: 'label',
      },
      {
        headerName: 'Catalogue Number',
        field: 'catNumbers',
      },
      {
        headerName: 'Format',
        field: 'format',
      },
      {
        headerName: 'Type',
        field: 'type',
      },
    ],
    []
  );

  const [detailVisibleColumns, setDetailVisibleColumns] = useState<Set<string>>(
    () => {
      const visible = new Set<string>();
      // Initialize with all non-hidden detail columns
      detailColumnDefsForVisibility.forEach((col) => {
        if (col.field && !col.hide) {
          visible.add(col.field);
        }
      });
      return visible;
    }
  );

  const initialColumnState = useMemo<ColumnState[]>(
    () =>
      DEFAULT_SORT_MODEL.filter(
        (item): item is Required<Pick<SortModelItem, 'colId' | 'sort'>> =>
          Boolean(item.colId && item.sort)
      ).map((item, index) => ({
        colId: item.colId,
        sort: item.sort as SortDirection,
        sortIndex: index,
      })),
    []
  );

  const defaultSortStateByColId = useMemo(
    () =>
      new Map<string, { sort: SortDirection; sortIndex: number }>(
        initialColumnState
          .filter(
            (
              col
            ): col is ColumnState & {
              sort: SortDirection;
              sortIndex: number;
            } =>
              col.sort !== null &&
              col.sortIndex !== null &&
              col.sort !== undefined &&
              col.sortIndex !== undefined
          )
          .map(({ colId, sort, sortIndex }) => [colId, { sort, sortIndex }])
      ),
    [initialColumnState]
  );

  const skipToLastPage = () => {
    const totalPages = Math.ceil(recordingsCount / rowsPerPage);
    if (totalPages > 0) {
      setPage(totalPages);
    }
  };

  const handleSortChanged = useCallback((event: SortChangedEvent<GridRow>) => {
    if (!event.api) {
      return;
    }

    // Ignore sort changes during initial sort application
    if (isApplyingInitialSortRef.current) {
      return;
    }

    // Get the current sort state from the grid
    const sortedColumns = event.api
      .getColumnState()
      .filter(
        (col) =>
          col.sort && col.sortIndex !== null && col.sortIndex !== undefined
      )
      .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0));

    // Build sortFields array from sorted columns
    const newSortFields: PrimaryDiscographyRequestSortFieldsInner[] =
      sortedColumns
        .map((col) => {
          if (!col.colId) return null;
          const apiField = GRID_SORT_TO_API_FIELD[col.colId];
          if (!apiField || !col.sort) return null;
          return {
            field: apiField,
            order: col.sort === 'desc' ? 'desc' : 'asc',
          };
        })
        .filter(
          (item): item is PrimaryDiscographyRequestSortFieldsInner =>
            item !== null
        );

    // Compare sort fields (order matters for multi-sort)
    const sortChanged =
      JSON.stringify(sortFieldsRef.current) !== JSON.stringify(newSortFields);

    // Only update if sort actually changed
    if (!sortChanged) {
      return;
    }

    // Update sortFields with new sort (can be empty array if user removed all sorts)
    sortFieldsRef.current = newSortFields;
    setSortFields(newSortFields);
    setPage(1);
  }, []);

  const handleFilterChanged = useCallback(
    (event: FilterChangedEvent<GridRow>) => {
      if (!event.api) {
        return;
      }

      const filterModel = event.api.getFilterModel();
      const newFieldFilters: PrimaryDiscographyRequestFieldFiltersInner[] = [];

      // Convert AG Grid filter model to API fieldFilters format
      // Note: filterModel only contains active filters, so if a filter is cleared, it won't be in the model
      Object.keys(filterModel).forEach((colId) => {
        const apiField = GRID_COL_TO_API_FILTER_FIELD[colId];
        if (!apiField) {
          return; // Skip columns that don't have filter mapping
        }

        const filter = filterModel[colId];
        if (!filter) {
          return; // Skip if filter is null/undefined
        }

        // Handle checkbox filter (AgCheckboxFilter returns { values: string[] })
        if (filter && filter.values && Array.isArray(filter.values)) {
          // Only add filter if at least one value is selected (filter is active)
          // When no values are selected, the filter is inactive and we don't filter
          if (filter.values.length > 0) {
            // Filter out the BLANK_KEY if present, as it's not a real value
            const realValues = filter.values.filter(
              (v: string) => v !== '__AG_CHECKBOX_FILTER_BLANK__'
            );
            if (realValues.length > 0) {
              // Special handling for featured column: expand "Yes" to ["featured", "otherFeatured"]
              let expandedValues = realValues;
              if (colId === 'featured') {
                expandedValues = realValues.flatMap((v: string) => {
                  if (v === 'Yes') {
                    return ['featured', 'otherFeatured'];
                  }
                  return [v];
                });
              }
              const filterConfig: PrimaryDiscographyRequestFieldFiltersInner = {
                field: apiField,
                value: expandedValues,
              };
              if (colId === 'featured') {
                filterConfig.caseSensitive = true;
              }
              newFieldFilters.push(filterConfig);
            }
          }
          // If filter.values.length === 0, filter is inactive, so we don't add it to fieldFilters
        } else if (filter && filter.filter !== undefined) {
          // AG Grid text filter stores the filter value in the 'filter' property
          const filterValue = String(filter.filter).trim();
          // Only add filter if there's a non-empty value
          if (filterValue) {
            newFieldFilters.push({
              field: apiField,
              value: [filterValue],
            });
          }
          // If filterValue is empty, don't add to fieldFilters (filter is cleared)
        }
      });

      // Compare with existing filters to prevent unnecessary updates
      const newFieldFiltersKey = JSON.stringify(newFieldFilters);
      const currentFieldFiltersKey = JSON.stringify(fieldFilters);

      // Only update if filters actually changed
      if (newFieldFiltersKey !== currentFieldFiltersKey) {
        setFieldFilters(newFieldFilters);
        setPage(1);
      }
    },
    [fieldFilters]
  );

  // Helper function to get available values for a column from API filters
  const getAvailableValuesForColumn = useCallback(
    (colId: string): string[] | null => {
      if (!availableFilters) {
        return null;
      }

      const apiField = GRID_COL_TO_API_FILTER_FIELD[colId];
      if (!apiField) {
        return null;
      }

      const filterData = availableFilters.availableFilters.find(
        (f) => f.field === apiField
      );
      return filterData?.values ?? null;
    },
    [availableFilters]
  );

  const handleGridReady = useCallback(
    (params: GridReadyEvent<GridRow>) => {
      gridApiRef.current = params.api;

      // Only apply initial sort state on first mount
      if (isInitialMountRef.current) {
        isApplyingInitialSortRef.current = true;
        params.api.applyColumnState({
          defaultState: { sort: null, sortIndex: null },
          state: initialColumnState,
        });
        isApplyingInitialSortRef.current = false;
        isInitialMountRef.current = false;
      }
    },
    [initialColumnState]
  );

  // Show a transient "copied" popup, auto-dismissing after a short delay.
  const showCopyFeedback = useCallback((message: string) => {
    setCopyFeedback(message);
    if (copyFeedbackTimeoutRef.current) {
      clearTimeout(copyFeedbackTimeoutRef.current);
    }
    copyFeedbackTimeoutRef.current = setTimeout(() => {
      setCopyFeedback(null);
      copyFeedbackTimeoutRef.current = null;
    }, 1800);
  }, []);

  useEffect(() => {
    return () => {
      if (copyFeedbackTimeoutRef.current) {
        clearTimeout(copyFeedbackTimeoutRef.current);
      }
    };
  }, []);

  // Remember the last cell the user clicked (in the main grid) so we know what
  // to copy. Tracking clicks is more reliable than AG Grid's focus reporting
  // with the interactive cell renderers in this grid.
  const handleCellClicked = useCallback((event: CellClickedEvent<GridRow>) => {
    if (event.rowIndex != null) {
      copySourceRef.current = {
        api: event.api,
        rowIndex: event.rowIndex,
        colId: event.column.getColId(),
      };
    }
  }, []);

  // Same, but for a cell clicked inside an expanded detail grid (the detail
  // grid is a separate AG Grid instance with its own api).
  const handleDetailCellClicked = useCallback(
    (api: GridApi<DetailRowData>, rowIndex: number, colId: string) => {
      copySourceRef.current = { api, rowIndex, colId };
    },
    []
  );

  // Clipboard copy for ag-grid-community (the built-in clipboard is an
  // Enterprise feature). We listen for Ctrl/Cmd+C on the document in the
  // capture phase — more reliable than AG Grid's onCellKeyDown, which doesn't
  // fire dependably when cells contain interactive renderers. The copy targets
  // the last clicked cell (main grid or expanded detail grid), preferring that
  // grid's focused cell. Ctrl/Cmd+C copies the cell; adding Shift copies the row.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const isCopyKey =
        (event.ctrlKey || event.metaKey) &&
        (event.key === 'c' || event.key === 'C');
      if (!isCopyKey) {
        return;
      }
      // Let the browser handle a normal text selection copy.
      if (window.getSelection()?.toString()) {
        return;
      }
      // Don't hijack copy while the user is typing in an input.
      const active = document.activeElement as HTMLElement | null;
      if (
        active &&
        (active.tagName === 'INPUT' ||
          active.tagName === 'TEXTAREA' ||
          active.isContentEditable)
      ) {
        return;
      }

      const source = copySourceRef.current;
      if (!source) {
        return;
      }

      try {
        const api = source.api as GridApi<unknown>;
        // Prefer the grid's own focused cell, fall back to the recorded click.
        let rowIndex = source.rowIndex;
        let column: Column | null = api.getColumn(source.colId);
        const focusedCell = api.getFocusedCell();
        if (focusedCell) {
          rowIndex = focusedCell.rowIndex;
          column = focusedCell.column;
        }
        if (!column) {
          return;
        }
        const rowNode = api.getDisplayedRowAtIndex(rowIndex);
        if (!rowNode) {
          return;
        }

        const payload = buildCopyPayload(api, rowNode, column, event.shiftKey);
        if (!payload) {
          return;
        }

        event.preventDefault();
        void copyTextToClipboard(payload.text).then((copied) => {
          if (copied) {
            // Brief highlight + popup so the user sees what was copied.
            api.flashCells({
              rowNodes: [rowNode],
              columns: payload.flashColumns,
            });
            showCopyFeedback(payload.feedback);
          } else {
            console.error('Failed to copy to clipboard');
          }
        });
      } catch {
        // The source grid may have been unmounted (e.g. the row was
        // collapsed) — ignore and let the next click re-establish a target.
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [showCopyFeedback]);

  const handleDetailGridSortChange = useCallback(
    (colId: string, sort: SortDirection | null) => {
      if (!gridApiRef.current) {
        return;
      }

      // Apply the sort to this column as primary sort
      // First, clear all sorts
      gridApiRef.current.applyColumnState({
        defaultState: { sort: null, sortIndex: null },
      });

      // Then apply the new sort
      if (sort) {
        gridApiRef.current.applyColumnState({
          state: [
            {
              colId,
              sort,
              sortIndex: 0,
            },
          ],
        });
      }
      // handleSortChanged will be triggered automatically and update sortFields
    },
    []
  );

  const handleDetailColumnVisibilityChange = useCallback(
    (field: string, visible: boolean) => {
      setDetailVisibleColumns((prev) => {
        const newSet = new Set(prev);
        if (visible) {
          newSet.add(field);
        } else {
          newSet.delete(field);
        }
        return newSet;
      });

      // Apply to all detail grids
      detailGridApiRefs.current.forEach((api) => {
        api.setColumnsVisible([field], visible);
      });
    },
    []
  );

  const resetSortOrder = useCallback(() => {
    if (!gridApiRef.current) {
      return;
    }

    gridApiRef.current.applyColumnState({
      defaultState: { sort: null, sortIndex: null },
      state: initialColumnState,
    });

    // handleSortChanged will be triggered automatically and update sortFields
    // But we also need to update sortFields immediately for consistency
    const defaultSortFields = DEFAULT_SORT_MODEL.map((item) => {
      const apiField = GRID_SORT_TO_API_FIELD[item.colId];
      if (apiField && item.sort) {
        return {
          field: apiField,
          order: item.sort === 'desc' ? 'desc' : 'asc',
        };
      }
      return null;
    }).filter(
      (item): item is PrimaryDiscographyRequestSortFieldsInner => item !== null
    );

    sortFieldsRef.current = defaultSortFields;
    setSortFields(defaultSortFields);
    setPage(1);
  }, [initialColumnState]);

  const resetFilters = useCallback(() => {
    if (!gridApiRef.current) {
      return;
    }

    // Clear all filters from the grid
    gridApiRef.current.setFilterModel(null);

    // Reset field filters state
    setFieldFilters([]);
    setPage(1);
  }, []);

  const toggleRowExpansion = useCallback((rowId: string) => {
    setExpandedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  }, []);

  const expandAllRows = useCallback(() => {
    setExpandedRowIds(new Set(rows.map((row) => row.id)));
  }, [rows]);

  const collapseAllRows = useCallback(() => {
    setExpandedRowIds(new Set());
  }, []);

  const hasAnyExpandedRows = useMemo(() => {
    return expandedRowIds.size > 0;
  }, [expandedRowIds.size]);

  // --- Bulk / mass editing handlers (PROTOTYPE) ----------------------------
  const toggleRowSelection = useCallback((rowId: string) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  }, []);

  // Select-all toggles between "every current row" and "none".
  const toggleSelectAll = useCallback(() => {
    setSelectedRowIds((prev) => {
      const allIds = rowsRef.current.map((row) => row.id);
      const allSelected =
        allIds.length > 0 && allIds.every((id) => prev.has(id));
      return allSelected ? new Set() : new Set(allIds);
    });
  }, []);

  const enterBulkEditMode = useCallback(() => {
    preBulkRowsPerPageRef.current = rowsPerPage;
    setSelectedRowIds(new Set());
    collapseAllRows();
    // Filtering is disabled in bulk mode — clear any active filters/search so
    // "all results" really means every record.
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setFieldFilters([]);
    setSelectedConfidenceCategory(null);
    gridApiRef.current?.setFilterModel(null);
    // Collapse pagination into a single page holding every record.
    setPage(1);

    // If we don't already hold every record, we're about to fetch all of them.
    // Show the loading indicator *immediately* and drop the current partial
    // page so the stale "current page" rows aren't shown while the full fetch
    // is in flight (avoids the "current page, then all rows" flash).
    const alreadyHaveAll =
      recordingsCount > 0 && rowsRef.current.length >= recordingsCount;
    if (!alreadyHaveAll) {
      setIsLoading(true);
      setRows([]);
    }
    setRowsPerPage(recordingsCount > 0 ? recordingsCount : rowsPerPage);
    setBulkEditMode(true);
  }, [rowsPerPage, recordingsCount, collapseAllRows]);

  const exitBulkEditMode = useCallback(() => {
    setBulkEditMode(false);
    setSelectedRowIds(new Set());
    setShowBulkEditModal(false);
    setRowsPerPage(preBulkRowsPerPageRef.current);
    setPage(1);
  }, []);

  // Cancel mass editing: discard all pending edits (which drops the mode back to
  // normal, since mass edit mode is derived from having pending edits). We stay
  // on the current page and keep the current filters — mass edit never changed
  // them, so there's nothing to restore.
  const exitMassEditMode = useCallback(() => {
    setMassEdits({});
    massEditsRef.current = {};
    setMassFieldTarget(null);
    setShowMassSummary(false);
    setMassIsrcReassignments({});
    setMassApplyResult(null);
  }, []);

  // Apply every pending mass edit through the batch endpoints (contract §3.1):
  // one call per endpoint rather than one per recording. Recordings that saved
  // cleanly are updated optimistically and their pending edits dropped; ones
  // that failed keep their edits so the user can fix and retry, with the
  // per-recording reasons shown in the summary.
  const handleApplyMassEdits = useCallback(async () => {
    if (!userId) return;
    setMassSaving(true);
    setMassApplyResult(null);
    try {
      const editsSnapshot = massEditsRef.current;
      const result = await applyMassEdits(
        api,
        userId,
        editsSnapshot,
        rowsRef.current,
        massIsrcReassignments
      );
      setMassApplyResult(result);

      if (result.appliedRecordingIds.length > 0) {
        const applied = new Set(result.appliedRecordingIds);
        setRows((prev) =>
          prev.map((r) =>
            applied.has(r.id) && editsSnapshot[r.id]
              ? projectMassEditsOntoRow(r, editsSnapshot[r.id])
              : r
          )
        );
        // Drop the edits that landed; anything still pending failed and stays
        // on screen so the user can correct it.
        setMassEdits((prev) => {
          const next = Object.fromEntries(
            Object.entries(prev).filter(([id]) => !applied.has(id))
          );
          massEditsRef.current = next;
          return next;
        });
      }

      if (result.ok) {
        exitMassEditMode();
      }
    } finally {
      setMassSaving(false);
    }
  }, [api, userId, massIsrcReassignments, exitMassEditMode]);

  // The first mass edit closes any open single-recording edit panel/modal so the
  // two editing surfaces don't overlap.
  const prevMassEditModeRef = useRef(massEditMode);
  useEffect(() => {
    if (massEditMode && !prevMassEditModeRef.current) {
      setShowEditModal(false);
      setShowEditPanel(false);
      setSelectedRowForModal(null);
    }
    prevMassEditModeRef.current = massEditMode;
  }, [massEditMode]);

  // Populate the contribution alias dropdown when a mass contributions modal
  // opens (personas are client-level, so any parent recording id works). Reuses
  // the same alias state as the bulk editor.
  useEffect(() => {
    if (
      massFieldTarget?.field !== 'contributions' ||
      !userId ||
      bulkClientAliases.length > 0
    ) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response =
          await api.lookupClientAliasesLookupClientAliasesResponse({
            userId,
            parentRecordingId: massFieldTarget.rowId,
          });
        if (!cancelled) {
          setBulkClientAliases(
            response.personas.map((p) => ({
              stageName: p.stageName,
              personaId: p.personaId,
            }))
          );
        }
      } catch {
        // Silently ignore — the alias dropdown will just be empty.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [massFieldTarget, userId, bulkClientAliases.length, api]);

  // Fetch client aliases (personas) when the bulk edit modal opens so the
  // contribution editor's Alias dropdown is populated. Personas are
  // client-level; the lookup just needs any parent recording id, so we use the
  // first selected row.
  useEffect(() => {
    if (!showBulkEditModal || !userId || bulkClientAliases.length > 0) return;
    const firstSelectedId = Array.from(selectedRowIds)[0];
    if (!firstSelectedId) return;

    let cancelled = false;
    (async () => {
      try {
        const response =
          await api.lookupClientAliasesLookupClientAliasesResponse({
            userId,
            parentRecordingId: firstSelectedId,
          });
        if (!cancelled) {
          setBulkClientAliases(
            response.personas.map((p) => ({
              stageName: p.stageName,
              personaId: p.personaId,
            }))
          );
        }
      } catch {
        // Silently ignore — the alias dropdown will just be empty.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    showBulkEditModal,
    userId,
    selectedRowIds,
    bulkClientAliases.length,
    api,
  ]);
  // -------------------------------------------------------------------------

  const handleOpenEditModal = useCallback((row: Row) => {
    // If a different recording is already open with unsaved changes, confirm
    // before switching so the user doesn't silently lose their edits.
    if (
      selectedRowIdRef.current &&
      selectedRowIdRef.current !== row.id &&
      editFormRef.current?.isDirty()
    ) {
      setPendingSwitchRow(row);
      return;
    }
    setSelectedRowForModal(row);
    setShowEditPanel(true);
  }, []);

  const handleSaveModalEdit = useCallback(
    (updatedRow: Row, reassignedIsrcs?: ReassignedIsrc[]) => {
      // Build a map of recordingId → ISRCs to remove (from duplicate ISRC reassignment)
      const isrcsToRemove = new Map<string, Set<string>>();
      if (reassignedIsrcs) {
        for (const { isrc, fromRecordingIds } of reassignedIsrcs) {
          for (const rid of fromRecordingIds) {
            if (!isrcsToRemove.has(rid)) isrcsToRemove.set(rid, new Set());
            isrcsToRemove.get(rid)!.add(isrc);
          }
        }
      }

      setRows((prevRows) => {
        return prevRows.map((row) => {
          if (row.id === updatedRow.id) {
            return { ...row, ...updatedRow, isModified: true };
          }
          // Strip reassigned ISRCs from source recordings
          const toRemove = isrcsToRemove.get(row.id);
          if (toRemove) {
            const newIsrcsArray = (row.isrcsArray ?? []).filter(
              (v) => !toRemove.has(v)
            );
            return {
              ...row,
              isrcs: newIsrcsArray.join(', '),
              isrcsArray: newIsrcsArray,
            };
          }
          return row;
        });
      });
      // Keep the modal/panel open after save so users can continue reviewing
      // their changes; the form drops back into read-only mode on its own and
      // also re-fetches the manual-changes overlay so indicators update.
      setSelectedRowForModal((prev) =>
        prev && prev.id === updatedRow.id
          ? { ...prev, ...updatedRow, isModified: true }
          : prev
      );
    },
    []
  );

  const gridRows = useMemo<GridRow[]>(() => {
    const expanded = new Set(expandedRowIds);
    const result: GridRow[] = [];

    rows.forEach((row) => {
      result.push({ ...row, kind: 'data' });
      if (expanded.has(row.id)) {
        result.push({
          kind: 'detail',
          id: `${row.id}__detail`,
          parentId: row.id,
          base: row,
        });
      }
    });

    return result;
  }, [expandedRowIds, rows]);

  const getRowId = useCallback((params: { data: GridRow | undefined }) => {
    if (!params.data) {
      return '';
    }
    if (params.data.kind === 'detail') {
      return params.data.id;
    }
    return params.data.id;
  }, []);

  const getRowHeight = useCallback((params: RowHeightParams<GridRow>) => {
    if (!params.data) {
      return 48;
    }

    if (params.data.kind === 'detail') {
      // Header height (30) + row height (70) + padding (24) = 124
      return 94;
    }

    return 48;
  }, []);

  const handlePostSortRows = useCallback(
    ({ nodes }: PostSortRowsParams<GridRow>) => {
      const detailNodes: typeof nodes = [];

      for (let i = nodes.length - 1; i >= 0; i--) {
        const node = nodes[i];
        if (node.data?.kind === 'detail') {
          detailNodes.push(node);
          nodes.splice(i, 1);
        }
      }

      detailNodes.reverse().forEach((detailNode) => {
        const data = detailNode.data;
        if (!data || data.kind !== 'detail') {
          nodes.push(detailNode);
          return;
        }

        const parentId = data.parentId;
        if (!parentId) {
          nodes.push(detailNode);
          return;
        }

        const parentIndex = nodes.findIndex(
          (node) => node.data?.kind === 'data' && node.data.id === parentId
        );

        if (parentIndex === -1) {
          nodes.push(detailNode);
          return;
        }

        nodes.splice(parentIndex + 1, 0, detailNode);
      });
    },
    []
  );

  useEffect(() => {
    setExpandedRowIds((prev) => {
      const available = new Set(rows.map((row) => row.id));
      const retained = new Set<string>();
      prev.forEach((id) => {
        if (available.has(id)) {
          retained.add(id);
        }
      });
      return retained.size === prev.size ? prev : retained;
    });
  }, [rows]);

  // Reset row heights when confidence filter changes and new data arrives
  // This fixes the visual bug where rows render with wrong heights after filter change
  const prevConfidenceCategoryRef = useRef<string | null>(null);
  useEffect(() => {
    // Check if confidence category changed
    const confidenceChanged =
      prevConfidenceCategoryRef.current !== selectedConfidenceCategory;

    if (confidenceChanged && gridApiRef.current && rows.length > 0) {
      // Reset row heights after confidence filter change to fix visual bug
      // Use requestAnimationFrame to ensure this happens after React render
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (gridApiRef.current) {
            gridApiRef.current.resetRowHeights();
          }
        });
      });
    }

    prevConfidenceCategoryRef.current = selectedConfidenceCategory;
  }, [selectedConfidenceCategory, rows.length]);

  const fetchData = async () => {
    if (userId === undefined) {
      return;
    }

    setIsLoading(true);

    try {
      const discographyData = await api.searchPrimaryDiscography({
        userId: userId,
        primaryDiscographyRequest: {
          size: rowsPerPage,
          fromOffset: (page - 1) * rowsPerPage,
          sortFields,
          ...(sortFields.length > 0 && {
            sortField: sortFields[0].field,
            sortOrder: sortFields[0].order,
          }),
          confidenceCategory: selectedConfidenceCategory ?? undefined,
          searchTerm: debouncedSearchTerm || undefined,
          fieldFilters: fieldFilters.length > 0 ? fieldFilters : undefined,
        },
      });

      const rows: Row[] = discographyData.recordings.map((recording): Row => {
        const {
          parentRecording,
          earliestParentRelease,
          evidenceReleases,
          westburyContributions,
          performance,
          persona,
        } = recording;
        return {
          id: parentRecording.id,
          artist: parentRecording.bandArtistName,
          title: parentRecording.recordingTitle,
          version: (parentRecording.recordingVersion ?? []).join(', '), // Version not available in API, set as null
          release: earliestParentRelease?.releaseTitle ?? '',
          year: earliestParentRelease?.dateReleased,
          isrcs: (parentRecording.isrcs ?? []).join(', '),
          contributions: westburyContributions.sort((a, b) =>
            b.confidenceCategory.localeCompare(a.confidenceCategory)
          ),
          duration: parentRecording.duration ?? null,
          isFeatured: ['featured', 'otherFeatured'].includes(
            performance.featuredStatus ?? ''
          ),
          isLive: !!parentRecording.live,
          live: parentRecording.live ?? null,
          // Subheadings data
          collaborators: '', // Do not remove the invisible space charater " "
          catNumbers: (earliestParentRelease?.catNumbers ?? []).join(', '),
          label: (earliestParentRelease?.releaseLabels ?? []).join(', '),
          format: earliestParentRelease?.format ?? '',
          mediaType: earliestParentRelease?.mediaType ?? '',
          stageName: persona.stageName,
          location: parentRecording.countryOfRecording ?? '',
          source: recording.parentRecording.source.toString(),
          labelsArray: earliestParentRelease?.releaseLabels ?? [],
          catNumbersArray: earliestParentRelease?.catNumbers ?? [],
          versionsArray: parentRecording.recordingVersion ?? [],
          isrcsArray: parentRecording.isrcs ?? [],
          mainArtistsRaw: parentRecording.mainArtists ?? [],
          documentId: recording.documentId ?? undefined,
          earliestParentReleaseId: earliestParentRelease?.id,
          sourceReleaseId: earliestParentRelease?.sourceReleaseId ?? [],
          // notes: '',
          earliestReleaseImageKey: (
            evidenceReleases?.at(0)?.evidenceImages ?? []
          )
            .map((image) => image.objectKey)
            .at(0),
          releaseImageKeys: (evidenceReleases ?? []).flatMap((release) =>
            (release.evidenceImages ?? []).map((image) => image.objectKey)
          ),
          hasManualChanges: false,
        };
      });

      // Fetch the manual-changes overlay for the visible rows. Contract 4.5.1.
      // We use document_ids (canonical OS identifier) when available; fall
      // back to recording_ids otherwise. Failures are non-fatal — the table
      // just renders without the purple-line indicator.
      const documentIds = rows
        .map((r) => r.documentId)
        .filter((d): d is string => !!d);
      const recordingIds = rows.map((r) => r.id);
      const changedRecordingIds = new Set<string>();
      try {
        if (userId) {
          const bulk = await api.bulkManualChangesBulkManualChangesResponse({
            userId,
            bulkManualChangesRequest:
              documentIds.length === rows.length
                ? { documentIds }
                : { recordingIds },
          });
          for (const entry of bulk?.recordings ?? []) {
            if (entry?.hasManualChanges && entry?.recordingId) {
              changedRecordingIds.add(entry.recordingId);
            }
          }
        }
      } catch (err) {
        console.warn('bulk manual-changes lookup failed', err);
      }
      for (const row of rows) {
        row.hasManualChanges = changedRecordingIds.has(row.id);
      }

      // For rows that have *some* manual change, resolve which specific fields
      // were manually edited (live, duration). This drives the write-once
      // editability gate in mass/bulk edit — a value that is already set and
      // has never been manually edited is locked (mirrors the edit form's
      // isFieldEdited). Only the changed subset needs the per-recording detail
      // call, so this stays bounded and off the happy path.
      if (userId) {
        const changedRows = rows.filter((r) => r.hasManualChanges);
        await Promise.all(
          changedRows.map(async (row) => {
            try {
              const detail =
                await api.recordingManualChangesRecordingManualChangesResponse({
                  userId,
                  parentRecordingId: row.id,
                  documentId: row.documentId,
                });
              const fields = (detail?.fields ?? {}) as Record<string, unknown>;
              row.liveEditedManually = fields.live != null;
              row.durationEditedManually = fields.duration != null;
              // Artists added by a manual change, so the mass-edit chips can
              // show the same purple line the edit form does (4.5.2).
              const artistItems =
                ((fields.artists as { items?: unknown } | undefined)?.items as
                  | { op?: string; artistId?: string; stageName?: string }[]
                  | undefined) ?? [];
              const added = artistItems.filter((it) => it.op === 'add');
              row.manuallyAddedArtistIds = added
                .map((it) => it.artistId)
                .filter((id): id is string => !!id);
              row.manuallyAddedArtistNames = added
                .map((it) => it.stageName)
                .filter((n): n is string => !!n);
              // Contribution roles touched by a manual change — mirrors the
              // edit form's editedContributionRoles (add or edit ops).
              const contributionItems =
                ((fields.contributions as { items?: unknown } | undefined)
                  ?.items as { op?: string; role?: string }[] | undefined) ??
                [];
              row.manuallyChangedContributionRoles = contributionItems
                .filter((it) => it.op === 'add' || it.op === 'edit')
                .map((it) => it.role)
                .filter((r): r is string => !!r);
              // ISRCs added by a manual change — matched by value, mirroring
              // the edit form's addedValuesFor('isrcs').
              const isrcItems =
                ((fields.isrcs as { items?: unknown } | undefined)?.items as
                  | { op?: string; value?: unknown }[]
                  | undefined) ?? [];
              row.manuallyAddedIsrcs = isrcItems
                .filter((it) => it.op === 'add' && typeof it.value === 'string')
                .map((it) => it.value as string);
            } catch (err) {
              console.warn('per-field manual-changes lookup failed', err);
            }
          })
        );
      }

      // Merge with existing rows to preserve editing state
      setRows((prevRows) => {
        return rows.map((newRow) => {
          const existingRow = prevRows.find((r) => r.id === newRow.id);
          if (existingRow && existingRow.isModified) {
            return {
              ...newRow,
              ...existingRow,
              // Keep modified values but update other fields
              artist: existingRow.artist,
              title: existingRow.title,
              release: existingRow.release,
              year: existingRow.year,
              isrcs: existingRow.isrcs,
            };
          }
          return {
            ...newRow,
            isModified: false,
            originalValues: {},
          };
        });
      });
      setRecordingsCount(discographyData.count);
    } catch (e) {
      console.error(e);
      setLoadingError(true);
    }

    setIsLoading(false);
  };

  const transformConfidenceStatsToChartData = (stats: ConfidenceStats) => {
    const getCategoryIcon = (categoryId: string): React.ReactNode => {
      switch (categoryId) {
        case '1':
          return <CircleCheck className="tw-w-5 tw-h-5" />;
        case '2':
          return <InfoIcon className="tw-w-5 tw-h-5" />;
        case '3':
          return (
            <div className="tw-p-1 tw-rounded-full tw-border-[#f97316] tw-border-[2px] tw-w-5 tw-h-5">
              <Flag className="tw-w-2.5 tw-h-2.5 -tw-mt-[1px] -tw-ml-[1px]" />
            </div>
          );
        case '4':
          return <HelpCircleIcon className="tw-w-5 tw-h-5" />;
        case '5':
          return <CircleX className="tw-w-5 tw-h-5" />;
        default:
          return <HelpCircleIcon className="tw-w-5 tw-h-5" />;
      }
    };

    const getCategoryInfo = (categoryId: string) => {
      switch (categoryId) {
        case '1':
          return {
            label: 'Verified (No Effort)',
            text: 'black',
            color: '#6EE038',
            description:
              'Contributions that have been verified and require no additional effort',
          };
        case '2':
          return {
            label: 'Inferred (Low Effort)',
            text: 'black',
            color: '#F6FF00',
            description:
              'Contributions that can be inferred with low verification effort',
          };
        case '3':
          return {
            label: 'Credited (Mid Effort)',
            text: 'white',
            color: '#FF8A00',
            description:
              'Contributions that are credited and require moderate verification effort',
          };
        case '4':
          return {
            label: 'Unverified (High Effort)',
            text: 'white',
            color: '#FF2600',
            description:
              'Contributions that are unverified and require high verification effort',
          };
        case '5':
          return {
            label: 'Unknown (Max Effort)',
            text: 'black',
            color: '#AAB2B9',
            description:
              'Contributions with unknown status requiring maximum verification effort',
          };
        default:
          return {
            label: `Category ${categoryId}`,
            text: 'white',
            color: '#AAB2B9',
            description: 'Unknown confidence category',
          };
      }
    };

    stats.confidenceCategories.sort(
      (a, b) => Number(a.confidenceCategory) - Number(b.confidenceCategory)
    );

    return stats.confidenceCategories.map((category) => {
      const categoryInfo = getCategoryInfo(category.confidenceCategory);
      return {
        id: category.confidenceCategory,
        label: categoryInfo.label,
        value: category.count,
        text: categoryInfo.text,
        color: categoryInfo.color,
        icon: getCategoryIcon(category.confidenceCategory),
        description: categoryInfo.description,
      };
    });
  };

  const fetchConfidenceStats = async () => {
    if (userId === undefined) {
      return;
    }

    try {
      const stats = await api.confidenceStatsConfidenceStats({ userId });
      setConfidenceStats(stats);
    } catch (e) {
      console.error('Failed to fetch confidence stats:', e);
      // Set to null on error so we can show default data or handle gracefully
      setConfidenceStats(null);
    }
  };

  const fetchAvailableFilters = async () => {
    if (userId === undefined) {
      return;
    }

    try {
      const filters = await api.availableFiltersAvailableFiltersResponse({
        userId,
      });
      setAvailableFilters(filters);
    } catch (e) {
      console.error('Failed to fetch available filters:', e);
      setAvailableFilters(null);
    }
  };

  const handleConfidenceFilterChange = (categoryId: string | null) => {
    // Filtering is disabled in bulk-edit mode — ignore chart clicks so the
    // single-page "all records" view isn't re-filtered.
    if (bulkEditMode) {
      return;
    }
    setSelectedConfidenceCategory(categoryId);
    setPage(1); // Reset to first page when filter changes
    // Don't clear rows immediately - let fetchData handle it naturally
    // This prevents AG Grid from having stale row nodes with cached heights
    setIsLoading(true); // Set loading state
  };

  useEffect(() => {
    fetchData();
  }, [
    userId,
    page,
    rowsPerPage,
    sortFieldsKey,
    selectedConfidenceCategory,
    debouncedSearchTerm,
    fieldFiltersKey,
  ]);

  useEffect(() => {
    fetchConfidenceStats();
    fetchAvailableFilters();
  }, [userId]);

  // Client personal info (used only for the header name) doesn't change with
  // pagination/sort/filter, so fetch it once per user. Keeping it inside
  // fetchData meant it re-fired on every page change and, being unhandled,
  // a transient network failure surfaced as an unhandled rejection that
  // crashed the whole view.
  useEffect(() => {
    if (userId === undefined) {
      return;
    }
    api
      .retrieveAdminPersonalInformation({ userId })
      .then((personalInfo) => {
        setClientPersonalInfo(personalInfo);
      })
      .catch((err) => {
        console.warn('failed to load client personal information', err);
      });
  }, [userId]);

  // Debounce search term with 300ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Reset to page 1 when debounced search term changes (but not on initial mount)
  useEffect(() => {
    if (prevDebouncedSearchTermRef.current !== debouncedSearchTerm) {
      if (!isInitialMountRef.current) {
        setPage(1);
      }
      prevDebouncedSearchTermRef.current = debouncedSearchTerm;
      isInitialMountRef.current = false;
    }
  }, [debouncedSearchTerm]);

  // Update filter available values when availableFilters changes
  useEffect(() => {
    if (!gridApiRef.current || !availableFilters) {
      return;
    }

    // Update each filterable column's filter with new available values
    const filterableColumns = [
      'artist',
      'version',
      'contributions',
      'release',
      'year',
      'live',
      'stageName',
      'type',
      'format',
      'label',
      'catNumbers',
      'featured',
    ];
    filterableColumns.forEach((colId) => {
      const column = gridApiRef.current?.getColumn(colId);
      if (!column) {
        return;
      }

      // Use getColumnApi or direct access to get filter instance
      const filterInstance = (gridApiRef.current as any).getFilterInstance?.(
        colId
      );
      if (
        filterInstance &&
        typeof filterInstance.setAvailableValues === 'function'
      ) {
        const availableValues = getAvailableValuesForColumn(colId);
        if (availableValues) {
          filterInstance.setAvailableValues(availableValues);
        }
      }
    });
  }, [availableFilters, getAvailableValuesForColumn]);

  const fetchSignedEvidenceImageUrls = () => {
    if (userId === undefined) {
      return;
    }
    for (const row of rows) {
      if (row.earliestReleaseImageKey === undefined) {
        continue;
      }
      api
        .evidenceImageSignedUrlEvidenceImageSignedUrl({
          userId,
          evidenceImageSignedUrlRequest: {
            objectKey: row.earliestReleaseImageKey,
          },
        })
        .then(({ signedUrl }) => {
          setImages((prev) => {
            return { ...prev, [row.id]: signedUrl };
          });
        })
        .catch((e) => {
          console.error(e);
        });
    }
  };

  useEffect(() => {
    fetchSignedEvidenceImageUrls();
  }, [rows]);

  const fetchLatestExportStatus = async () => {
    if (userId === undefined) {
      return;
    }
    api
      .retrieveLatestPrimaryDiscographyExport({ userId })
      .then((primaryDiscographyExport) => {
        setIsPendingExportGeneration(!primaryDiscographyExport.ready);
      })
      .catch(() => {});
  };

  useEffect(() => {
    fetchLatestExportStatus();
  }, [userId]);

  const handleGenerateExport = () => {
    if (userId === undefined) {
      return;
    }
    api
      .createGeneratePrimaryDiscographyExport({ userId })
      .then(() => {
        setIsOpenExportConfirmationModal(true);
        fetchLatestExportStatus();
      })
      .catch(() => {
        throwError(new WError('Discography export generation request failed'));
      });
  };

  const defaultColDef = useMemo<ColDef<GridRow>>(
    () => ({
      sortable: true,
      resizable: true,
      suppressMenu: true,
      suppressMovable: false,
      sortingOrder: ['asc', 'desc', null],
      flex: 1,
      minWidth: 80,
    }),
    []
  );

  // Store images in a ref so cell renderers can access it without causing columnDefs to recreate
  const imagesRef = useRef<RowIdImageUrlMap>(images);
  const prevImagesKeysRef = useRef<string>(JSON.stringify(Object.keys(images)));
  useEffect(() => {
    const currentImagesKeys = JSON.stringify(Object.keys(images));
    imagesRef.current = images;

    // Refresh cells when images change (including initial load)
    // Check if images actually changed to avoid unnecessary refreshes
    if (gridApiRef.current && currentImagesKeys !== prevImagesKeysRef.current) {
      prevImagesKeysRef.current = currentImagesKeys;
      // Use setTimeout to ensure this happens after render
      setTimeout(() => {
        if (gridApiRef.current) {
          gridApiRef.current.refreshCells({
            columns: ['evidence'],
            force: true,
          });
        }
      }, 0);
    }
  }, [images]);

  // Store expandedRowIds in a ref so cell renderers can access it without causing columnDefs to recreate
  const expandedRowIdsRef = useRef<Set<string>>(expandedRowIds);
  useEffect(() => {
    expandedRowIdsRef.current = expandedRowIds;
  }, [expandedRowIds]);

  // Custom header component that shows sort index next to sort icon
  // Uses AG Grid's native sort handling via setSort method
  // Defined outside useMemo to prevent recreation
  const CustomHeaderWithSortIndex: React.FC<IHeaderParams<GridRow>> = (
    params
  ) => {
    // Use state to force re-render when sort changes
    const [sortState, setSortState] = useState<{
      sort: SortDirection;
      sortIndex: number | null | undefined;
    }>(() => ({
      sort: params.column?.getSort() ?? null,
      sortIndex: params.column?.getSortIndex() ?? null,
    }));

    // Track filter state
    const [isFiltered, setIsFiltered] = useState<boolean>(() => {
      if (!params.column) return false;
      const filterModel = params.api.getFilterModel();
      const colId = params.column.getColId();
      const filter = filterModel[colId];

      // Check for text filter
      if (filter && filter.filter) {
        return true;
      }

      // Check for checkbox filter (AgCheckboxFilter returns { values: string[] })
      if (
        filter &&
        filter.values &&
        Array.isArray(filter.values) &&
        filter.values.length > 0
      ) {
        return true;
      }

      return false;
    });

    // Track menu open state to properly toggle (use ref to avoid re-renders in event handler)
    const menuOpenRef = useRef<boolean>(false);
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

    // Listen to sort changes and update state to force re-render
    useEffect(() => {
      const updateSortState = () => {
        setSortState({
          sort: params.column?.getSort() ?? null,
          sortIndex: params.column?.getSortIndex() ?? null,
        });
      };

      const updateFilterState = () => {
        if (!params.column) return;
        const filterModel = params.api.getFilterModel();
        const colId = params.column.getColId();
        const filter = filterModel[colId];

        // Check for text filter
        let hasFilter = false;
        if (filter && filter.filter) {
          hasFilter = true;
        } else if (
          filter &&
          filter.values &&
          Array.isArray(filter.values) &&
          filter.values.length > 0
        ) {
          // Check for checkbox filter with selected values
          hasFilter = true;
        }

        setIsFiltered(hasFilter);
      };

      // Update filter state when filter changes (don't update menu state here to avoid loops)
      // AG Grid automatically closes the menu when filters are applied
      const handleFilterChanged = () => {
        updateFilterState();
        // Update ref without causing re-render
        menuOpenRef.current = false;
      };

      // Update immediately
      updateSortState();
      updateFilterState();

      // Listen to sort changed events
      params.api.addEventListener('sortChanged', updateSortState);
      // Listen to filter changed events (only update filter state, not menu state)
      params.api.addEventListener('filterChanged', handleFilterChanged);

      return () => {
        params.api.removeEventListener('sortChanged', updateSortState);
        params.api.removeEventListener('filterChanged', handleFilterChanged);
      };
    }, [params.api, params.column]);

    const sortIndex = sortState.sortIndex;
    const sort = sortState.sort;
    const displayName = params.displayName;

    // Handle sort click - use AG Grid's native setSort method
    const onSortRequested = (
      event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
    ) => {
      if (!params.enableSorting || !params.column) {
        return;
      }

      // Check for Ctrl (Windows/Linux) or Cmd (Mac) for multi-sort
      // The grid is configured with multiSortKey="ctrl"
      const mouseEvent = event as React.MouseEvent<HTMLDivElement>;
      const multiSortKey = mouseEvent.ctrlKey || mouseEvent.metaKey;

      // Use AG Grid's native setSort method which handles multi-sort automatically
      // The multiSortKey="ctrl" setting will be respected
      params.setSort(
        sort === 'asc' ? 'desc' : sort === 'desc' ? null : 'asc',
        multiSortKey
      );
    };

    // Handle filter icon click - toggle filter menu
    const onFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();
      if (!params.column) {
        return;
      }
      // If menu is open, close it; otherwise open it
      // Use ref to check state to avoid stale closures
      if (menuOpenRef.current) {
        // Close the menu
        params.api.hidePopupMenu();
        menuOpenRef.current = false;
        setIsMenuOpen(false);
      } else {
        // Open the column menu which contains the filter
        params.showColumnMenu(event.currentTarget);
        menuOpenRef.current = true;
        setIsMenuOpen(true);
      }
    };

    return (
      <div
        className={`ag-header-cell-label tw-flex tw-items-center tw-gap-1 tw-w-full ${
          params.enableSorting ? 'tw-cursor-pointer' : ''
        }`}
        onClick={params.enableSorting ? onSortRequested : undefined}
        onTouchEnd={params.enableSorting ? onSortRequested : undefined}
        role={params.enableSorting ? 'button' : undefined}
        tabIndex={params.enableSorting ? 0 : undefined}
        onKeyDown={
          params.enableSorting
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSortRequested(e as any);
                }
              }
            : undefined
        }
        aria-label={params.enableSorting ? `Sort by ${displayName}` : undefined}
      >
        <span className="tw-flex-1">{displayName}</span>
        <div className="tw-flex tw-items-center tw-gap-1">
          {/* Filter icon - show if column has filter enabled */}
          {params.column?.getColDef().filter && (
            <button
              type="button"
              onClick={onFilterClick}
              className={`tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-rounded tw-transition-colors ${
                isFiltered
                  ? 'tw-bg-blue-600 tw-text-white tw-p-1'
                  : 'tw-bg-transparent tw-text-neutral-500 tw-p-1 hover:tw-bg-neutral-800'
              }`}
              aria-label={`Filter ${displayName}`}
            >
              <Filter
                className={`tw-w-3 tw-h-3 ${
                  isFiltered ? 'tw-text-white' : 'tw-text-neutral-500'
                }`}
              />
            </button>
          )}
          {/* Sort controls - show if sorting is enabled */}
          {params.enableSorting && (
            <>
              {sort && sortIndex !== null && sortIndex !== undefined && (
                <span className="tw-text-xs tw-text-neutral-400 tw-font-medium tw-h-[14px]">
                  {sortIndex + 1}
                </span>
              )}
              {sort ? (
                <div className="tw-flex tw-items-center">
                  {sort === 'asc' ? (
                    <ArrowUp className="tw-w-3 tw-h-3" />
                  ) : (
                    <ArrowDown className="tw-w-3 tw-h-3" />
                  )}
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    );
  };

  const ExpandAllHeaderComponent: React.FC<IHeaderParams<GridRow>> = () => {
    return null;
  };

  // Header checkbox for the bulk-edit selection column: select all / none.
  // Reads current selection from the ref; the selection effect calls
  // refreshHeader() so this stays in sync.
  const SelectAllHeaderComponent: React.FC<IHeaderParams<GridRow>> = () => {
    const allIds = rowsRef.current.map((row) => row.id);
    const selected = selectedRowIdsRef.current;
    const allSelected =
      allIds.length > 0 && allIds.every((id) => selected.has(id));
    return (
      <div className="tw-flex tw-h-full tw-items-center tw-justify-center">
        <WCheckBox
          checkboxSize="sm"
          checked={allSelected}
          onChange={toggleSelectAll}
        />
      </div>
    );
  };

  const columnDefs = useMemo<ColDef<GridRow>[]>(() => {
    return [
      {
        headerName: '',
        colId: 'expand',
        width: 20,
        maxWidth: 20,
        cellStyle: (params) => {
          const baseStyle = {
            padding: 0,
            display: 'flex',
          };
          if (params.data?.kind === 'detail') {
            return {
              ...baseStyle,
              alignItems: 'stretch',
              justifyContent: 'stretch',
              width: '100%',
            };
          }
          return {
            ...baseStyle,
            alignItems: 'center',
            justifyContent: 'center',
          };
        },
        headerClass: 'tw-flex tw-items-center tw-justify-center',
        flex: 0,
        sortable: false,
        resizable: false,
        suppressMovable: true,
        headerComponent: ExpandAllHeaderComponent,
        cellRenderer: (params: ICellRendererParams<GridRow>) => {
          if (!params.data) {
            return null;
          }

          if (params.data.kind === 'detail') {
            const row = params.data.base;
            return (
              <DetailGrid
                row={row}
                onSortChange={handleDetailGridSortChange}
                rowId={params.data.parentId}
                onGridReady={(rowId, api) => {
                  detailGridApiRefs.current.set(rowId, api);
                }}
                visibleColumns={detailVisibleColumns}
                onCellClickedForCopy={handleDetailCellClicked}
              />
            );
          }

          const isExpanded = expandedRowIdsRef.current.has(params.data.id);

          return (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                toggleRowExpansion(params.data!.id);
              }}
              // translateX nudges the chevron right so it clears the
              // "manually changed" left line with a small gap. A transform is
              // used (rather than margin/padding/width) so it doesn't reflow
              // the column or shift the rest of the row layout.
              className="tw-flex tw-h-7 tw-w-7 tw-translate-x-[8px] tw-items-center tw-justify-center tw-rounded tw-border tw-border-[#30353b] tw-bg-transparent tw-text-white hover:tw-border-[#4a5056]"
              aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
            >
              {isExpanded ? (
                <ChevronDown className="tw-h-4 tw-w-4" />
              ) : (
                <ChevronRight className="tw-h-4 tw-w-4" />
              )}
            </button>
          );
        },
        colSpan: (params: ColSpanParams<GridRow>) => {
          if (params.data?.kind === 'detail') {
            return Math.max(params.api.getAllDisplayedColumns().length, 1);
          }
          return 1;
        },
      },
      {
        headerName: 'Evidence',
        colId: 'evidence',
        width: 80,
        minWidth: 100,
        flex: 0,
        sortable: false,
        cellRenderer: (params: ICellRendererParams<GridRow>) => {
          if (!params.data || params.data.kind !== 'data') {
            return null;
          }

          const row = params.data;
          const imageUrl = imagesRef.current[row.id];
          const hasImageKey = !!row.earliestReleaseImageKey;

          return (
            <div className="tw-w-[45px] tw-h-[45px]">
              {hasImageKey ? (
                <Link to={`/discography/${userId}/ai-evidence/${row.id}`}>
                  <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-cursor-pointer">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt="Evidence"
                        className="tw-w-full tw-h-full"
                      />
                    ) : (
                      <div className="tw-flex tw-justify-center">
                        <p className="tw-my-auto tw-animate-ping">⚪</p>
                      </div>
                    )}
                  </div>
                </Link>
              ) : (
                <div className="tw-w-full tw-h-full tw-flex tw-justify-center tw-items-center">
                  <i className="wi wi-shut-eye wi-20px" />
                </div>
              )}
            </div>
          );
        },
        colSpan: (params: ColSpanParams<GridRow>) => {
          if (params.data?.kind === 'detail') {
            return 0;
          }
          return 1;
        },
      },
      {
        headerName: 'Artist',
        colId: 'artist',
        field: 'artist',
        width: 160,
        sortable: true,
        filter: AgCheckboxFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.artist : '',
        filterValueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.artist : null,
        filterParams: {
          availableValues: getAvailableValuesForColumn('artist') ?? undefined,
          serverSideFiltering: true,
        },
      },
      {
        headerName: 'Recording Title',
        colId: 'title',
        field: 'title',
        width: 128,
        minWidth: 200,
        sortable: true,
        filter: AgTextFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.title : '',
        filterParams: {
          serverSideFiltering: true,
        },
      },
      {
        headerName: 'Version',
        hide: false,
        colId: 'version',
        field: 'version',
        width: 50,
        minWidth: 150,
        sortable: true,
        filter: AgCheckboxFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.version || '-' : '',
        filterValueGetter: (params) => {
          if (params.data?.kind !== 'data') {
            return null;
          }
          return params.data.versionsArray?.length
            ? params.data.versionsArray
            : null;
        },
        filterParams: {
          availableValues: getAvailableValuesForColumn('version') ?? undefined,
          serverSideFiltering: true,
        },
        cellRenderer: (params: ICellRendererParams<GridRow>) => {
          if (params.data?.kind !== 'data') {
            return null;
          }
          return <span>{params.data.version || '-'}</span>;
        },
      },
      {
        headerName: 'Contributions',
        colId: 'contributions',
        field: 'contributions',
        minWidth: 150,
        width: 150,
        sortable: true,
        filter: AgCheckboxFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.contributions : [],
        filterValueGetter: (params) => {
          if (params.data?.kind !== 'data') {
            return null;
          }
          // Return as array for better filtering - split comma-separated values
          const contributions = params.data.contributions;
          if (!contributions) {
            return null;
          }
          // Split by comma and trim each value
          return contributions.map((contribution) => contribution.contribution);
        },
        filterParams: {
          availableValues:
            getAvailableValuesForColumn('contributions') ?? undefined,
          serverSideFiltering: true,
        },
        cellRenderer: (params: ICellRendererParams<GridRow>) => {
          if (params.data?.kind !== 'data') {
            return null;
          }
          return (
            <div className="tw-flex tw-gap-2">
              {params.data.contributions.map((contribution) => (
                <ContributionPill
                  key={contribution.contribution}
                  contribution={contribution}
                />
              ))}
            </div>
          );
        },
      },
      {
        headerName: 'ISRCs',
        colId: 'isrcs',
        field: 'isrcs',
        width: 50,
        sortable: true,
        filter: AgTextFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.isrcs : '',
        filterParams: {
          serverSideFiltering: true,
        },
      },
      {
        headerName: 'Release',
        colId: 'release',
        field: 'release',
        width: 50,
        minWidth: 160,
        sortable: true,
        filter: AgCheckboxFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.release : '',
        filterValueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.release : null,
        filterParams: {
          availableValues: getAvailableValuesForColumn('release') ?? undefined,
          serverSideFiltering: true,
        },
      },
      {
        headerName: 'Year',
        colId: 'year',
        field: 'year',
        width: 80,
        sortable: true,
        filter: AgCheckboxFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) => {
          if (params.data?.kind !== 'data' || !params.data.year) {
            return '';
          }
          return new Date(params.data.year).getFullYear().toString();
        },
        filterValueGetter: (params) => {
          if (params.data?.kind !== 'data' || !params.data.year) {
            return null;
          }
          return new Date(params.data.year).getFullYear().toString();
        },
        filterParams: {
          availableValues: getAvailableValuesForColumn('year') ?? undefined,
          serverSideFiltering: true,
          // Extract year from date strings like "1999-01-23"
          valueExtractor: (apiValue: string) => {
            try {
              const date = new Date(apiValue);
              if (!isNaN(date.getTime())) {
                return date.getFullYear().toString();
              }
            } catch {
              // If parsing fails, return as-is
            }
            return apiValue;
          },
        },
      },
      {
        headerName: 'Duration',
        colId: 'duration',
        field: 'duration',
        width: 100,
        sortable: true,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.duration || '-' : '',
        cellRenderer: (params: ICellRendererParams<GridRow>) => {
          if (params.data?.kind !== 'data') {
            return null;
          }
          return <span>{params.data.duration || '-'}</span>;
        },
      },
      {
        headerName: 'Type',
        hide: true,
        colId: 'type',
        field: 'mediaType',
        width: 120,
        sortable: true,
        filter: AgCheckboxFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.mediaType : '',
        filterValueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.mediaType : null,
        filterParams: {
          availableValues: getAvailableValuesForColumn('type') ?? undefined,
          serverSideFiltering: true,
        },
      },
      {
        headerName: 'Stage Name',
        hide: true,
        colId: 'stageName',
        field: 'stageName',
        width: 150,
        sortable: true,
        filter: AgCheckboxFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.stageName : '',
        filterValueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.stageName : null,
        filterParams: {
          availableValues:
            getAvailableValuesForColumn('stageName') ?? undefined,
          serverSideFiltering: true,
        },
      },
      {
        headerName: 'Live',
        colId: 'live',
        field: 'live',
        width: 150,
        minWidth: 120,
        sortable: true,
        filter: AgCheckboxFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        // `live` is a free-text descriptor string (e.g. "Live in London, 1989").
        // Empty/null means the recording is not live. Sorted/filtered
        // alphanumerically on the string, not the former boolean flag.
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.live || '-' : '',
        filterValueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.live || null : null,
        filterParams: {
          availableValues: getAvailableValuesForColumn('live') ?? undefined,
          serverSideFiltering: true,
        },
        cellRenderer: (params: ICellRendererParams<GridRow>) => {
          if (params.data?.kind !== 'data') {
            return null;
          }
          return <span>{params.data.live || '-'}</span>;
        },
      },
      {
        headerName: 'Featured',
        hide: true,
        colId: 'featured',
        field: 'isFeatured',
        width: 140,
        maxWidth: 140,
        sortable: true,
        filter: AgCheckboxFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.isFeatured : '',
        filterValueGetter: (params) => {
          if (params.data?.kind !== 'data') {
            return null;
          }
          // Map boolean to display value
          return params.data.isFeatured ? 'Yes' : 'No';
        },
        filterParams: {
          availableValues: getAvailableValuesForColumn('featured') ?? undefined,
          serverSideFiltering: true,
          caseSensitive: true,
          // Map API values to display values
          valueMapper: (apiValue: string) => {
            if (apiValue === 'featured' || apiValue === 'otherFeatured')
              return 'Yes';
            if (apiValue === 'nonFeatured') return 'No';
            return apiValue;
          },
          // Map display values back to API values (will be expanded in handleFilterChanged)
          reverseMapper: (displayValue: string) => {
            // Keep "Yes" as-is, it will be expanded to ["featured", "otherFeatured"] in handleFilterChanged
            if (displayValue === 'Yes') return 'Yes';
            if (displayValue === 'No') return 'nonFeatured';
            return displayValue;
          },
        },
        cellRenderer: (params: ICellRendererParams<GridRow>) => {
          if (params.data?.kind !== 'data') {
            return null;
          }
          return (
            <div>
              <i
                className={clsx('wi wi-xs', {
                  'wi-check': params.data.isFeatured,
                  'wi-close': !params.data.isFeatured,
                })}
              />
            </div>
          );
        },
      },
      {
        headerName: 'Format',
        hide: true,
        colId: 'format',
        field: 'format',
        width: 120,
        sortable: true,
        filter: AgCheckboxFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.format : '',
        filterValueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.format : null,
        filterParams: {
          availableValues: getAvailableValuesForColumn('format') ?? undefined,
          serverSideFiltering: true,
          caseInsensitiveDeduplication: true,
        },
      },
      {
        headerName: 'Collaborators',
        colId: 'collaborators',
        field: 'collaborators',
        hide: true,
        minWidth: 100,
        sortable: true,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Label',
        colId: 'label',
        field: 'label',
        hide: true,
        minWidth: 100,
        sortable: true,
        filter: AgCheckboxFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.label : '',
        filterValueGetter: (params) => {
          if (params.data?.kind !== 'data') {
            return null;
          }
          return params.data.labelsArray?.length
            ? params.data.labelsArray
            : null;
        },
        filterParams: {
          availableValues: getAvailableValuesForColumn('label') ?? undefined,
          serverSideFiltering: true,
        },
      },
      {
        headerName: 'Catalogue Number',
        colId: 'catNumbers',
        field: 'catNumbers',
        hide: true,
        minWidth: 100,
        sortable: true,
        filter: AgCheckboxFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.catNumbers : '',
        filterValueGetter: (params) => {
          if (params.data?.kind !== 'data') {
            return null;
          }
          return params.data.catNumbersArray?.length
            ? params.data.catNumbersArray
            : null;
        },
        filterParams: {
          availableValues:
            getAvailableValuesForColumn('catNumbers') ?? undefined,
          serverSideFiltering: true,
        },
      },
      {
        headerName: 'Actions',
        colId: 'actions',
        width: 120,
        minWidth: 120,
        maxWidth: 120,
        flex: 0,
        sortable: false,
        resizable: false,
        cellClass: 'tw-flex tw-items-center tw-justify-center',
        cellRenderer: (params: ICellRendererParams<GridRow>) => {
          if (!params.data || params.data.kind !== 'data') {
            return null;
          }
          const row = params.data;
          return (
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
              <WButton
                icon="edit"
                variant="secondary"
                label="Edit"
                onClick={() => handleOpenEditModal(row)}
              />
            </div>
          );
        },
      },
    ];
  }, [
    userId,
    toggleRowExpansion,
    handleDetailGridSortChange,
    getAvailableValuesForColumn,
    handleOpenEditModal,
  ]);

  // Column defs actually handed to the grid.
  //  • Bulk-edit mode prepends a selection checkbox column, drops Actions, and
  //    disables filtering.
  //  • Otherwise every recording-level cell is hover-editable at all times (the
  //    edit pencil is revealed on hover). The moment the first edit lands, mass
  //    edit mode is active, and we additionally drop the Actions column and
  //    freeze filtering — matching the toolbar lockdown.
  const effectiveColumnDefs = useMemo<ColDef<GridRow>[]>(() => {
    if (bulkEditMode) {
      const selectColumn: ColDef<GridRow> = {
        headerName: '',
        colId: 'select',
        width: 48,
        minWidth: 48,
        maxWidth: 48,
        flex: 0,
        sortable: false,
        resizable: false,
        suppressMovable: true,
        // Intentionally NOT pinned: pinning splits the row into separate
        // pinned/centre viewports, which makes the manual-change inset
        // box-shadow (drawn per row element in `getRowStyle`) appear on both
        // sides of the checkbox. Keeping the column unpinned draws the
        // indicator once, on the left of the checkbox.
        headerComponent: SelectAllHeaderComponent,
        cellClass: 'tw-flex tw-items-center tw-justify-center',
        cellRenderer: (params: ICellRendererParams<GridRow>) => {
          if (!params.data || params.data.kind !== 'data') {
            return null;
          }
          const rowId = params.data.id;
          return (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
            <div
              className="tw-flex tw-h-full tw-items-center tw-justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <WCheckBox
                checkboxSize="sm"
                checked={selectedRowIdsRef.current.has(rowId)}
                onChange={() => toggleRowSelection(rowId)}
              />
            </div>
          );
        },
      };

      const bulkColumns = columnDefs.filter((col) => col.colId !== 'actions');
      return [selectColumn, ...bulkColumns].map((col) =>
        col.colId === 'select'
          ? col
          : { ...col, filter: false, floatingFilter: false }
      );
    }

    // Default (browsing + mass editing).
    const COMPLEX_FIELDS: Record<string, MassComplexField> = {
      artist: 'artist',
      version: 'version',
      isrcs: 'isrcs',
      contributions: 'contributions',
      live: 'live',
    };
    // The Actions (full edit form) column and filtering are only removed once
    // mass edit mode is active; before that, normal browsing is untouched.
    const dataColumns = massEditMode
      ? columnDefs.filter((col) => col.colId !== 'actions')
      : columnDefs;
    return dataColumns.map((col) => {
      const base: ColDef<GridRow> = massEditMode
        ? { ...col, filter: false, floatingFilter: false }
        : { ...col };

      // Recording Title — always editable, through its own modal rather than
      // an inline input. A title is required, and a cell that silently refuses
      // to commit an empty value can't say why; the modal states the rule and
      // disables Save instead.
      if (col.colId === 'title') {
        return {
          ...base,
          cellRenderer: (p: ICellRendererParams<GridRow>) => {
            if (p.data?.kind !== 'data') return null;
            const dataRow = p.data;
            const pend = massEditsRef.current[dataRow.id]?.title;
            const orig = dataRow.title ?? '';
            const value = pend !== undefined ? pend : orig;
            return (
              <MassComplexCellShell
                changed={pend !== undefined && pend !== orig}
                onOpen={() =>
                  setMassTextTarget({ rowId: dataRow.id, field: 'title' })
                }
              >
                <span className="tw-truncate">{value || '-'}</span>
              </MassComplexCellShell>
            );
          },
        };
      }

      // Duration — write-once (HH:MM:SS). Edited through its own modal rather
      // than an inline input: the format is easy to get wrong and the cell has
      // no room to explain the mistake. Editable when unset, or once it has
      // been manually changed before; a system-populated duration that was
      // never manually edited shows a disabled pencil, mirroring Live.
      if (col.colId === 'duration') {
        return {
          ...base,
          cellRenderer: (p: ICellRendererParams<GridRow>) => {
            if (p.data?.kind !== 'data') return null;
            const dataRow = p.data;
            const pend = massEditsRef.current[dataRow.id]?.duration;
            const orig = dataRow.duration ?? '';
            const value = pend !== undefined ? pend : orig;
            const locked =
              pend === undefined &&
              !isWriteOnceFieldEditable(
                dataRow.duration,
                !!dataRow.durationEditedManually
              );
            return (
              <MassComplexCellShell
                changed={pend !== undefined && pend !== orig}
                onOpen={() =>
                  setMassTextTarget({ rowId: dataRow.id, field: 'duration' })
                }
                disabled={locked}
                disabledReason={DURATION_LOCKED_REASON}
              >
                <span className="tw-truncate">{value || '-'}</span>
              </MassComplexCellShell>
            );
          },
        };
      }

      const complexField = COMPLEX_FIELDS[col.colId ?? ''];
      if (complexField) {
        return {
          ...base,
          cellRenderer: (p: ICellRendererParams<GridRow>) => {
            if (p.data?.kind !== 'data') return null;
            const row = p.data;
            const pend = massEditsRef.current[row.id]?.[complexField];
            // Versions can only be edited, not added — a recording with no
            // versions has nothing to edit, so it renders read-only (no edit
            // affordance).
            if (complexField === 'version') {
              const hasVersions =
                pend !== undefined
                  ? (pend as string[]).length > 0
                  : (row.versionsArray?.length ?? 0) > 0;
              if (!hasVersions) {
                return (
                  <MassCellShell changed={false}>
                    <span className="tw-text-textDove">-</span>
                  </MassCellShell>
                );
              }
            }
            // Live is write-once: a descriptor that is already set can only be
            // changed if it was manually changed before. A pending in-session
            // edit keeps it open so the user can revise their own change.
            const liveLocked =
              complexField === 'live' &&
              typeof pend !== 'string' &&
              !isWriteOnceFieldEditable(
                liveToString(row.live),
                !!row.liveEditedManually
              );
            return (
              <MassComplexCellShell
                changed={pend !== undefined}
                onOpen={() => openMassFieldModal(row.id, complexField)}
                disabled={liveLocked}
                disabledReason={LIVE_LOCKED_REASON}
              >
                {renderMassComplexContent(complexField, row, pend)}
              </MassComplexCellShell>
            );
          },
        };
      }

      // Release-level / non-editable columns: unchanged (aside from the
      // mass-mode filter strip applied in `base`).
      return base;
    });
  }, [
    bulkEditMode,
    massEditMode,
    columnDefs,
    toggleRowSelection,
    setMassCellEdit,
    openMassFieldModal,
  ]);

  // Highlight row currently being edited, and visually indicate modified rows
  const getRowStyle = useCallback(
    (params: RowClassParams<GridRow>): RowStyle | undefined => {
      if (params.data?.kind !== 'data') return undefined;
      const row = params.data;
      const isBeingEdited =
        selectedRowForModal != null && row.id === selectedRowForModal.id;
      const isBulkSelected =
        bulkEditMode && selectedRowIdsRef.current.has(row.id);
      const hasManualEdits = row.hasManualChanges || row.isModified;
      const style: RowStyle = {};
      if (isBeingEdited) {
        style.backgroundColor = '#262626';
      } else if (isBulkSelected) {
        // Slightly lighter background across the whole row when it is selected
        // for bulk editing.
        style.backgroundColor = '#2f2f2f';
      }
      // Purple left line when the recording has any tracked manual edits
      // (sourced from the bulk manual-changes endpoint, contract 4.5.1).
      // We treat `isModified` (just-saved in this session) the same way so
      // optimistic edits get the same indicator as edits returned by the BE.
      // The indicator must persist while the row is being edited, otherwise
      // selecting a manually-changed recording would drop its purple line.
      if (hasManualEdits) {
        style.boxShadow = MANUAL_CHANGE_BOX_SHADOW;
      }
      return isBeingEdited || isBulkSelected || hasManualEdits
        ? style
        : undefined;
    },
    [selectedRowForModal, bulkEditMode]
  );

  // Contributions & artists currently assigned across the selected recordings —
  // used to power the bulk "Delete" and "Replace" pickers so the user can only
  // act on values that are actually present.
  const bulkAssigned = useMemo(() => {
    const contributions = new Set<string>();
    const artists = new Set<string>();
    let firstRow: Row | null = null;
    rows.forEach((row) => {
      if (!selectedRowIds.has(row.id)) return;
      if (!firstRow) firstRow = row;
      row.contributions?.forEach((c) => {
        if (c.contribution) contributions.add(c.contribution);
      });
      if (row.artist) artists.add(row.artist);
    });
    return {
      contributions: Array.from(contributions).sort(),
      artists: Array.from(artists).sort(),
      firstRow: firstRow as Row | null,
    };
  }, [rows, selectedRowIds]);

  return (
    <PageLayout>
      <EditRecordingModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedRowForModal(null);
        }}
        row={selectedRowForModal}
        onSave={handleSaveModalEdit}
        userId={userId}
        availableFormats={allReleaseTypes}
        availableTypes={allReleaseFormats}
        availableContributions={allContributionRoles}
        availableArtists={getAvailableValuesForColumn('artist') ?? []}
      />
      <ExportConfirmationModal
        isOpen={isOpenExportConfirmationModal}
        onClose={() => setIsOpenExportConfirmationModal(false)}
      />
      <BulkEditModal
        isOpen={showBulkEditModal}
        onClose={() => setShowBulkEditModal(false)}
        selectedCount={selectedRowIds.size}
        availableArtists={getAvailableValuesForColumn('artist') ?? []}
        availableContributions={allContributionRoles}
        availableAliases={bulkClientAliases}
        assignedContributions={bulkAssigned.contributions}
        assignedArtists={bulkAssigned.artists}
        userId={userId}
        releaseContextRow={bulkAssigned.firstRow}
        availableFormats={allReleaseTypes}
        availableTypes={allReleaseFormats}
      />
      <MassEditSummaryModal
        isOpen={showMassSummary}
        onClose={() => {
          if (massSaving) return;
          setShowMassSummary(false);
          setMassApplyResult(null);
        }}
        onConfirm={handleApplyMassEdits}
        edits={massEdits}
        rows={rows}
        onEditChange={handleSummaryEditChange}
        onRemoveChange={removeMassCellEdit}
        isSaving={massSaving}
        errorsByRecording={massApplyResult?.errorsByRecording}
        fatalError={massApplyResult?.fatalError}
      />
      {/* Complex-field quick-edit modal — used both from the grid and from the
          review summary's Edit action. Rendered after the summary so it stacks
          on top when opened from there. */}
      {massFieldTarget &&
        (() => {
          const targetRow = rows.find((r) => r.id === massFieldTarget.rowId);
          if (!targetRow) return null;
          return (
            <MassEditFieldModal
              field={massFieldTarget.field}
              row={targetRow}
              pending={massEdits[targetRow.id]?.[massFieldTarget.field]}
              availableContributions={allContributionRoles}
              availableAliases={bulkClientAliases}
              userId={userId}
              onClose={() => setMassFieldTarget(null)}
              onSave={(field, value) =>
                handleMassFieldSave(targetRow, field, value)
              }
              onReassignIsrc={(reassignments) =>
                handleReassignIsrcs(targetRow.id, reassignments)
              }
            />
          );
        })()}
      {/* Title / Duration editors — opened from the grid's edit pencil and from
          the review summary's Edit action. */}
      {massTextTarget &&
        (() => {
          const targetRow = rows.find((r) => r.id === massTextTarget.rowId);
          if (!targetRow) return null;
          const field = massTextTarget.field;
          const pend = massEdits[targetRow.id]?.[field];

          if (field === 'duration') {
            return (
              <MassDurationEditModal
                recordingTitle={targetRow.title || '(untitled)'}
                initial={pend !== undefined ? pend : (targetRow.duration ?? '')}
                locked={
                  pend === undefined &&
                  !isWriteOnceFieldEditable(
                    targetRow.duration,
                    !!targetRow.durationEditedManually
                  )
                }
                onClose={() => setMassTextTarget(null)}
                onSave={(value) => {
                  handleMassTextSave(targetRow, field, value);
                  setMassTextTarget(null);
                }}
              />
            );
          }

          return (
            <MassTitleEditModal
              recordingTitle={targetRow.title || '(untitled)'}
              initial={pend !== undefined ? pend : (targetRow.title ?? '')}
              onClose={() => setMassTextTarget(null)}
              onSave={(value) => {
                handleMassTextSave(targetRow, field, value);
                setMassTextTarget(null);
              }}
            />
          );
        })()}
      <div
        className={`tw-flex tw-w-full tw-gap-4 ${
          showEditPanel && selectedRowForModal
            ? 'tw-mr-[calc(min(520px,95vw)+1rem)]'
            : ''
        }`}
      >
        <div
          ref={mainContentColumnRef}
          className={clsx(
            'tw-flex-1 tw-min-w-0 tw-flex tw-flex-col tw-gap-6',
            showEditPanel &&
              selectedRowForModal &&
              'tw-pr-[calc(min(520px,95vw)+1rem)]'
          )}
        >
          {/* Back Button */}
          <div className="tw-flex tw-mb-6">
            <WButton
              variant="link-secondary"
              icon="chevron-left"
              onClick={() => {
                window.history.back();
              }}
              label="Back"
            />
          </div>
          <div className="tw-flex tw-flex-col tw-gap-2 tw-w-full">
            <div className="tw-flex tw-flex-col tw-justify-between tw-w-full">
              <div>
                <h2>{`${clientPersonalInfo?.firstName} ${clientPersonalInfo?.lastName}`}</h2>
              </div>
              <div className="tw-flex-grow tw-flex tw-items-end tw-gap-4">
                {/* "Role Data Quality" chart stays visible in bulk-edit mode.
                    It also doubles as a filter, but bulk mode disables its
                    filter side-effect (handleConfidenceFilterChange no-ops). */}
                <VerificationChart
                  data={
                    confidenceStats
                      ? transformConfidenceStatsToChartData(confidenceStats)
                      : []
                  }
                  onFilterChange={handleConfidenceFilterChange}
                />
                {!bulkEditMode && (
                  <div className="tw-justify-self-end tw-mb-[18px]">
                    <WButton
                      variant="secondary"
                      label="Export Discography"
                      icon="export"
                      disabled={isPendingExportGeneration}
                      onClick={handleGenerateExport}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="tw-bg-surfaceBlack90 tw-p-6 tw-text-white tw-rounded-2xl tw-max-w-full IS_W_CONTAINER">
              {/* Fixed min-height matches the search input (h-12 = 48px) so the
                  row keeps its height when switching to/from bulk-edit mode and
                  the table below doesn't shift. */}
              <div className="tw-flex tw-justify-between tw-items-center tw-min-h-[48px]">
                {bulkEditMode ? (
                  <>
                    {/* Bulk-edit mode: search & normal buttons are hidden;
                        only bulk-editing actions remain. */}
                    <div className="tw-flex tw-items-center tw-gap-3">
                      <span className="tw-text-white tw-text-base tw-font-semibold">
                        Bulk edit
                      </span>
                      <span className="tw-text-sm tw-text-textDove">
                        {selectedRowIds.size} selected
                      </span>
                    </div>
                    <div className="tw-flex tw-gap-2">
                      <WButton
                        variant="primary"
                        label="Edit"
                        icon="edit"
                        disabled={selectedRowIds.size === 0}
                        onClick={() => setShowBulkEditModal(true)}
                      />
                      <WButton
                        variant="secondary"
                        label="Cancel"
                        onClick={exitBulkEditMode}
                      />
                    </div>
                  </>
                ) : massEditMode ? (
                  <>
                    {/* Mass-edit mode: search & normal buttons are hidden; only
                        Save (opens the change summary) and Cancel remain. */}
                    <div className="tw-flex tw-items-center tw-gap-3">
                      <span className="tw-text-white tw-text-base tw-font-semibold">
                        Mass edit
                      </span>
                      <span className="tw-text-sm tw-text-textDove">
                        {massEditFieldCount} pending change
                        {massEditFieldCount === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="tw-flex tw-gap-2">
                      <WButton
                        variant="primary"
                        label="Save"
                        disabled={massEditFieldCount === 0}
                        onClick={() => setShowMassSummary(true)}
                      />
                      <WButton
                        variant="secondary"
                        label="Cancel"
                        onClick={exitMassEditMode}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="tw-flex tw-flex-col tw-gap-2 tw-w-1/2">
                      <div className="tw-relative">
                        <div className="tw-relative">
                          <i
                            className={`wi wi-20px wi-search tw-absolute tw-left-3 tw-top-[14px]`}
                          ></i>
                          <input
                            className={clsx(
                              'tw-bg-formInputBackground',
                              'tw-text-gray-400',
                              'tw-rounded-lg',
                              'tw-w-full',
                              'tw-h-12',
                              'tw-placeholder-textSeal',
                              'tw-text-sm',
                              'tw-leading-5',
                              'tw-p-3',
                              'tw-pl-9',
                              'tw-border-2',
                              'tw-border-formInputBackground',
                              'focus:tw-border-white',
                              'focus:tw-text-white',
                              'focus:tw-outline-none'
                            )}
                            id="discography-search"
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search discography..."
                            autoComplete="off"
                          />
                        </div>
                        {searchTerm && (
                          <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-text-neutral-400 hover:tw-text-white tw-transition-colors"
                            aria-label="Clear search"
                          >
                            <X className="tw-w-4 tw-h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="tw-flex tw-gap-2">
                      {BULK_EDIT_ENABLED && (
                        <WButton
                          variant="primary"
                          label="Bulk Edit"
                          icon="edit"
                          onClick={enterBulkEditMode}
                        />
                      )}
                      <WButton
                        variant="secondary"
                        label="Expand/Collapse All"
                        onClick={() => {
                          if (hasAnyExpandedRows) {
                            collapseAllRows();
                          } else {
                            expandAllRows();
                          }
                        }}
                      />
                      <CombinedColumnVisibilityDropdown
                        gridApi={gridApiRef.current}
                        columnDefs={columnDefs}
                        detailColumnDefs={detailColumnDefsForVisibility}
                        onDetailColumnVisibilityChange={
                          handleDetailColumnVisibilityChange
                        }
                        detailVisibleColumns={detailVisibleColumns}
                      />
                      <WButton
                        variant="secondary"
                        label="Reset Sort"
                        onClick={resetSortOrder}
                      />
                      <WButton
                        variant="secondary"
                        label="Reset Filters"
                        onClick={resetFilters}
                      />
                    </div>
                  </>
                )}
              </div>
              <div>
                <div className="tw-h-[500px] tw-relative">
                  {copyFeedback && (
                    <div
                      role="status"
                      aria-live="polite"
                      className="tw-absolute tw-bottom-4 tw-left-1/2 -tw-translate-x-1/2 tw-z-50 tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-bg-neutral-800 tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-shadow-lg tw-pointer-events-none"
                    >
                      <CircleCheck className="tw-w-4 tw-h-4 tw-text-green-400" />
                      {copyFeedback}
                    </div>
                  )}
                  <AgGridReact
                    theme={myTheme}
                    rowData={gridRows}
                    columnDefs={effectiveColumnDefs}
                    defaultColDef={defaultColDef}
                    pagination={false}
                    onSortChanged={handleSortChanged}
                    onFilterChanged={handleFilterChanged}
                    onGridReady={handleGridReady}
                    onCellClicked={handleCellClicked}
                    postSortRows={handlePostSortRows}
                    getRowId={getRowId}
                    getRowHeight={getRowHeight}
                    getRowStyle={getRowStyle}
                    masterDetail={true}
                    animateRows={true}
                    domLayout="normal"
                    multiSortKey="ctrl"
                    loading={isLoading && rows.length === 0}
                    isExternalFilterPresent={() => {
                      // Tell AG Grid that filtering is handled server-side
                      // Check if any filters are active (fieldFilters, searchTerm, or confidenceCategory)
                      return (
                        fieldFilters.length > 0 ||
                        debouncedSearchTerm.length > 0 ||
                        selectedConfidenceCategory !== null
                      );
                    }}
                    doesExternalFilterPass={() => {
                      // Since filtering is handled server-side, always return true
                      // The backend has already filtered the data, so we show all rows returned
                      return true;
                    }}
                    overlayNoRowsTemplate={
                      loadingError
                        ? '<div class="ag-overlay-no-rows-center" style="color: white;"><h5>Error loading data</h5></div>'
                        : debouncedSearchTerm || selectedConfidenceCategory
                        ? '<div class="ag-overlay-no-rows-center" style="color: white;"><h5>The filters did not match any data</h5></div>'
                        : '<div class="ag-overlay-no-rows-center" style="color: white;"><h5>No data available currently</h5></div>'
                    }
                  />
                  {/* Keep existing rows visible but dimmed while a page
                      change / search / filter / sort request is in flight.
                      AG Grid's own `loading` overlay covers the empty
                      initial load (rows.length === 0). */}
                  <TableLoadingOverlay active={isLoading && rows.length > 0} />
                </div>
                {/* Pagination is hidden in bulk-edit mode (all records on one
                    page) and while mass editing (locked to the current page so
                    pending edits aren't lost by navigating away). */}
                {recordingsCount > 0 && !bulkEditMode && !massEditMode && (
                  <DataTablePagination
                    currentPage={page}
                    totalRows={recordingsCount}
                    rowsPerPage={rowsPerPage}
                    onPageChange={(newPage) => {
                      setPage(newPage);
                    }}
                    onRowsPerPageChange={(newRowsPerPage) => {
                      setRowsPerPage(newRowsPerPage);
                      setPage(1);
                    }}
                    onGoToLastPage={skipToLastPage}
                  />
                )}
                {bulkEditMode && recordingsCount > 0 && (
                  <div className="tw-mt-3 tw-text-sm tw-text-textDove">
                    Showing all {recordingsCount} recording
                    {recordingsCount === 1 ? '' : 's'} on a single page.
                  </div>
                )}
                {massEditMode && (
                  <div className="tw-mt-3 tw-text-sm tw-text-textDove">
                    Mass editing {rows.length} recording
                    {rows.length === 1 ? '' : 's'} on this page. Save or cancel
                    to continue browsing.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {showEditPanel && selectedRowForModal && (
          <aside
            className="tw-fixed tw-right-0 tw-w-[min(520px,95vw)] tw-border-l tw-border-gray-600 tw-bg-surfaceBlack90 tw-flex tw-flex-col tw-overflow-hidden tw-rounded-l-2xl tw-z-10"
            style={{
              boxShadow: '-4px 0 12px rgba(0,0,0,0.15)',
              ...(panelBounds
                ? { top: panelBounds.top, height: panelBounds.height }
                : { top: 64, height: 'calc(100vh - 124px)' }),
            }}
          >
            <div className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-border-b tw-border-gray-600 tw-flex-shrink-0">
              <h3 className="tw-text-white tw-text-base tw-font-semibold">
                Edit Recording
              </h3>
              <div className="tw-flex tw-gap-2">
                <WButton
                  variant="secondary"
                  label="Full screen"
                  onClick={() => setShowEditModal(true)}
                />
                <WButton
                  variant="secondary"
                  label="Close"
                  onClick={() => {
                    setShowEditPanel(false);
                    setSelectedRowForModal(null);
                  }}
                />
              </div>
            </div>
            <div className="tw-flex-1 tw-overflow-y-auto tw-min-h-0">
              <RecordingEditForm
                ref={editFormRef}
                row={selectedRowForModal}
                onSave={(r, reassignedIsrcs) => {
                  handleSaveModalEdit(r, reassignedIsrcs);
                  // Keep the panel open after save (form drops back to
                  // read-only on its own).
                }}
                onCancel={() => {
                  setShowEditPanel(false);
                  setSelectedRowForModal(null);
                }}
                stacked
                hideFooter
                userId={userId}
                availableFormats={allReleaseTypes}
                availableTypes={allReleaseFormats}
                availableContributions={allContributionRoles}
                availableArtists={getAvailableValuesForColumn('artist') ?? []}
              />
            </div>
          </aside>
        )}

        {/* Discard-changes confirmation when switching recordings */}
        <WModal
          isOpen={pendingSwitchRow !== null}
          onClose={() => setPendingSwitchRow(null)}
          title="Discard unsaved changes?"
          modalClasses="tw-max-w-md tw-w-full tw-mx-4"
        >
          <div className="tw-mt-4">
            <p className="tw-text-white tw-text-sm tw-mb-6">
              You have unsaved changes on this recording. Switching to another
              recording will discard them.
            </p>
            <div className="tw-flex tw-justify-end tw-gap-2">
              <WButton
                variant="secondary"
                label="Continue editing"
                onClick={() => setPendingSwitchRow(null)}
              />
              <WButton
                variant="primary"
                label="Discard changes"
                onClick={() => {
                  const next = pendingSwitchRow;
                  setPendingSwitchRow(null);
                  if (next) {
                    setSelectedRowForModal(next);
                    setShowEditPanel(true);
                  }
                }}
              />
            </div>
          </div>
        </WModal>
      </div>
    </PageLayout>
  );
}

interface EditRecordingModalProps {
  isOpen: boolean;
  onClose: () => void;
  row: Row | null;
  onSave: (row: Row, reassignedIsrcs?: ReassignedIsrc[]) => void;
  userId?: string;
  availableFormats?: string[];
  availableTypes?: string[];
  availableContributions?: string[];
  availableArtists?: string[];
}

const EditRecordingModal: React.FC<EditRecordingModalProps> = ({
  isOpen,
  onClose,
  row,
  onSave,
  userId,
  availableFormats = [],
  availableTypes = [],
  availableContributions = [],
  availableArtists = [],
}) => {
  if (!isOpen || !row) return null;
  return (
    <WModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Recording Details"
      modalClasses="tw-max-w-5xl tw-w-full tw-max-h-[90vh] tw-flex tw-flex-col tw-bg-gray-900 tw-overflow-hidden tw-mx-4"
    >
      <div className="tw-flex-1 tw-overflow-hidden tw-flex tw-flex-col tw-min-h-0">
        <RecordingEditForm
          row={row}
          onSave={(r, reassignedIsrcs) => {
            // Keep the modal open after save — the form drops back to
            // read-only mode on its own. The user closes it explicitly.
            onSave(r, reassignedIsrcs);
          }}
          onCancel={onClose}
          userId={userId}
          availableFormats={availableFormats}
          availableTypes={availableTypes}
          availableContributions={availableContributions}
          availableArtists={availableArtists}
        />
      </div>
    </WModal>
  );
};

interface ExportConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ExportConfirmationModal: React.FC<ExportConfirmationModalProps> = ({
  isOpen,
  onClose,
}) => {
  return (
    <WModal isOpen={isOpen} onClose={onClose} title="" hasCloseButton={true}>
      <div className="tw-pb-8">
        <ThankyouBlock
          title="Discography Export In Progress"
          description="Once complete, a download link will appear as a task on your
          dashboard."
        />
      </div>
    </WModal>
  );
};

interface DataTablePaginationProps {
  currentPage: number;
  totalRows: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onGoToLastPage: () => void;
}

const DataTablePagination: React.FC<DataTablePaginationProps> = ({
  currentPage,
  totalRows,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onGoToLastPage,
}) => {
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const startRow = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endRow = Math.min(currentPage * rowsPerPage, totalRows);
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage >= totalPages;

  // Calculate which page numbers to show
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage <= 3) {
        // Show first few pages
        for (let i = 2; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('ellipsis');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Show last few pages
        pages.push('ellipsis');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show pages around current
        pages.push('ellipsis');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('ellipsis');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="tw-flex tw-items-center tw-justify-between tw-mt-4 tw-text-white tw-text-sm">
      <div className="tw-flex tw-items-center tw-gap-2">
        <span className="tw-text-neutral-400">Rows per page:</span>
        <select
          value={rowsPerPage}
          onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
          className="tw-bg-[#151719] tw-border tw-border-[#535D66] tw-rounded tw-px-2 tw-py-1 tw-text-white focus:tw-outline-none focus:tw-border-[#4a5056]"
        >
          {[25, 50, 100, 500].map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>
      <div className="tw-flex tw-items-center tw-gap-4">
        <span className="tw-text-neutral-400 tw-text-sm">
          {startRow}-{endRow} of {totalRows}
        </span>
        <Pagination className="[&&]:tw-w-auto">
          <PaginationContent>
            <PaginationItem>
              <PaginationLink
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (!isFirstPage) {
                    onPageChange(1);
                  }
                }}
                className={
                  isFirstPage
                    ? 'tw-pointer-events-none tw-opacity-50'
                    : 'tw-cursor-pointer tw-gap-1 tw-px-2.5'
                }
                size="default"
              >
                <ChevronsLeft className="tw-h-4 tw-w-4" />
                <span className="tw-hidden sm:tw-inline-block">First</span>
              </PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (!isFirstPage) {
                    onPageChange(currentPage - 1);
                  }
                }}
                className={
                  isFirstPage
                    ? 'tw-pointer-events-none tw-opacity-50'
                    : 'tw-cursor-pointer'
                }
              />
            </PaginationItem>
            {pageNumbers.map((pageNum, index) => {
              if (pageNum === 'ellipsis') {
                return (
                  <PaginationItem key={`ellipsis-${index}`}>
                    <span className="tw-px-2 tw-text-neutral-400">...</span>
                  </PaginationItem>
                );
              }
              const pageNumber = pageNum as number;
              const isActive = pageNumber === currentPage;
              return (
                <PaginationItem key={pageNumber}>
                  <PaginationLink
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (!isActive) {
                        onPageChange(pageNumber);
                      }
                    }}
                    style={{
                      color: isActive ? 'black' : 'white',
                    }}
                    className={
                      isActive
                        ? 'tw-bg-white tw-text-black tw-pointer-events-none'
                        : 'tw-cursor-pointer'
                    }
                    size="default"
                  >
                    {pageNumber}
                  </PaginationLink>
                </PaginationItem>
              );
            })}
            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (!isLastPage) {
                    onPageChange(currentPage + 1);
                  }
                }}
                className={
                  isLastPage
                    ? 'tw-pointer-events-none tw-opacity-50'
                    : 'tw-cursor-pointer'
                }
              />
            </PaginationItem>
            <PaginationItem>
              <PaginationLink
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (!isLastPage) {
                    onGoToLastPage();
                  }
                }}
                className={
                  isLastPage
                    ? 'tw-pointer-events-none tw-opacity-50'
                    : 'tw-cursor-pointer tw-gap-1 tw-px-2.5'
                }
                size="default"
              >
                <span className="tw-hidden sm:tw-inline-block">Last</span>
                <ChevronsRight className="tw-h-4 tw-w-4" />
              </PaginationLink>
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>
  );
};

export default MasterDiscography;
